import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useProjectById, useBillableItems, useDeleteProject, useDeleteBillableItem, useProjectScopes, useUpdateProjectScope, useDeleteProjectScope, useAddScopeContact, useRemoveScopeContact, useSetPrimaryScopeContact, useSetInvoiceScopeContact, useOrganizations, useScopeTypes, useOrganizationContacts, useUpdateProjectInline, usePermits, useProjectCategoryOptions, useInvoices, useProjectCoverImage, useUpdateProjectCoverImage, useProjectFeeAssumptions } from '@/hooks';
import { useConvertToTemplate, useCopyTemplate, useSendProposal, useDeclineProposal, useRestoreProject } from '@/hooks/useProjects';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ProjectStageBadge } from '@/components/projects/ProjectStageBadge';
import { OrphanClientBanner } from '@/components/projects/OrphanClientBanner'; // Spec: keystone-client-required-per-category (R5)
import { WorkflowStageSelect } from '@/components/forms/WorkflowStageSelect';
import { ProjectScopesList } from '@/components/scopes/ProjectScopesList';
import { buildScopeUpdatePayload } from '@/lib/scope-payload'; // #174: scope-update PUT body (forwards establishment_id)
import { InvoiceChangeReview } from '@/components/invoices/InvoiceChangeReview';
import type { InvoiceChangeIntent } from '@/lib/invoiceSelectiveHistoryContracts';
import { PermitsList } from '@/components/permits/PermitsList';
import { DeliverablesSection } from '@/components/deliverables/DeliverablesSection'; // Phase 10b.7
import { useDeliverables } from '@/hooks/useDeliverables'; // Phase 10b.7
import { useOperationalSettings, useProposalDefaults } from '@/hooks/useSettings'; // B4: overdue threshold
import { SubmittalsSection } from '@/components/submittals/SubmittalsSection'; // Submittals module
import { SIsList } from '@/components/supplemental-instructions/SIsList'; // R3: Supplemental Instructions
import { useSubmittals } from '@/hooks/useSubmittals'; // Submittals module
import { SiteVisitsSection } from '@/components/site-visits/SiteVisitsSection'; // Phase 10d.1
import { useSiteVisits } from '@/hooks/useSiteVisits'; // Phase 10d.1
import { RFIsList } from '@/components/rfis/RFIsList'; // Phase 10d.2
import { useRFIs } from '@/hooks/useRFIs'; // Phase 10d.2
import { useSIs } from '@/hooks/useSIs'; // R3: Supplemental Instructions
import { DocumentsSection } from '@/components/documents/DocumentsSection'; // R1: Document management
import { ProjectMessagesPanel } from '@/components/projects/ProjectMessagesPanel'; // planTEK: message thread
import { MessagesSlideOver } from '@/components/projects/MessagesSlideOver'; // planTEK: header messages slide-over
import { useProjectMessages } from '@/hooks/useProjectMessages'; // planTEK: message thread
import { ProjectMembersSection } from '@/components/projects/ProjectMembersSection'; // Phase 10c.10
import { useProjectMembers } from '@/hooks/useProjectMembers'; // Phase 10c.10
import { ProjectInvoicesList } from '@/components/projects/ProjectInvoicesList'; // Phase 7.5.19
import SOVTab from '@/components/projects/SOVTab'; // Phase 11a
import { AddScopeModal } from '@/components/scopes/AddScopeModal';
import { ActionSummaryWidget } from '@/components/projects/ActionSummaryWidget';
import { BillingTracksPanel } from '@/components/projects/BillingTracksPanel'; // R5 (v0.14): per-scope billing tracks summary + safe-delete
import { SearchableOrganizationCombobox } from '@/components/organizations/SearchableOrganizationCombobox';
import { BillableItemsTable } from '@/components/billable-items/BillableItemsTable';
import { BillableItemTemplateQuickAdd } from '@/components/billable-items/BillableItemTemplateQuickAdd';
import { ConvertProposalModal } from '@/components/ConvertProposalModal';
// EditProjectDialog removed - all fields are inline-editable
// 9.7a: ClientContactSelect import removed - client contact now edited via ScopeCard
import type { WorkflowStage } from '@/components/forms/WorkflowStageSelect';
// 9.7a: ScopeStatusDropdown import removed - only type needed now
import type { ScopeStatus } from '@/components/scopes/ScopeStatusDropdown';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, Trash2, Archive, RotateCcw, Plus, Pencil, AlertTriangle, FileStack, FileText, Copy, Send, Download, Building2, Layers, DollarSign, FileCheck, Receipt, Mail, XCircle, Eye, Users, Camera, MessageSquare, ClipboardList, MessageCircle, ChevronDown, Globe, FolderOpen, MoreHorizontal } from 'lucide-react';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useProjectSectionPreferences } from '@/hooks/useProjectSectionPreferences';
import { useFirmType } from '@/hooks/useFirmType';
import { useVisibleSections } from '@/hooks/useVisibleSections';
import { CORE_SECTIONS, ADDON_SECTIONS, SECTION_METADATA } from '@/config/sectionMetadata';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AddModuleAffordance } from '@/components/projects/AddModuleAffordance';
import { HiddenSectionBanner } from '@/components/projects/HiddenSectionBanner';
import { useSectionConfig, readOverrides, computeNextOverrides } from '@/hooks/useSectionConfig';
import { ClientActionsSection } from '@/components/projects/ClientActionsSection'; // planTEK: client actions
import { useClientActions } from '@/hooks/useClientActions'; // Phase 10e: acceptance overdue widget
import type { OverdueAcceptanceItem } from '@/components/projects/ActionSummaryWidget'; // Phase 10e
import { ProposalPdfDownload } from '@/components/proposals/ProposalPdfDownload';
import { SendProposalButton } from '@/components/proposals/SendProposalButton';
import { EntityActionMenu } from '@/components/ui/EntityActionMenu';
import { EmailSendIndicator } from '@/components/email/EmailSendIndicator';
import type { ActionMenuItem } from '@/components/ui/EntityActionMenu';
import type { BillableItem } from '@/lib/schemas';
import { InlineEditActions } from '@/components/ui/InlineEditActions';
import { FinancialSummaryTable } from '@/components/projects/FinancialSummaryTable';
// 9.9d CONTRACT-03: FeeAssumptionsInput/Display replaced with FeeAssumptionsList
import { FeeAssumptionsList } from '@/components/FeeAssumptionsList';
import { isProposalStage } from '@/utils/status'; // Phase 9.5f: Use shared status utility
import { summarizeBillableItemsForSection } from '@/utils/billableItemsRollup'; // v0.14e: exclude cancelled from rollups
import { useReferrerPage } from '@/hooks/useReferrerPage'; // 9.15c: Navigation/pagination fix
import { ShareWithClientDialog } from '@/components/projects/ShareWithClientDialog'; // Phase 10d.3
import { CAScopeSection, type CAScopeConfig } from '@/components/proposals/CAScopeSection'; // V120: CA scope
import { ProjectPortalSection } from '@/components/projects/ProjectPortalSection'; // Portal improvements
import { useAuth } from '@/lib/auth/AuthProvider'; // Phase 10d.3: portal_management gate
import { useFeature } from '@/lib/features/FeatureProvider'; // planTEK: feature flags API
import { useQuery } from '@tanstack/react-query'; // Phase 10d.3: project share state
import { api } from '@/lib/api'; // Phase 10d.3: project share API

// 9.7a: ContactSelectInline component removed - client contact now edited via ScopeCard

import { ImageUpload } from '@/components/ui/ImageUpload';

/**
 * Maps a canonical section key to its `openSections` state key. Most are 1:1;
 * a few sections use a camelCase open-state key (e.g. site-visits → siteVisits).
 */
const SECTION_OPEN_KEY: Record<string, string> = {
  'site-visits': 'siteVisits',
  'client-actions': 'clientActions',
};

/**
 * Maps a canonical section key to the collapsible GROUP it renders inside
 * (non-template, grouped layout only — see the "Group: ..." blocks below).
 * Sections absent from this map (invoices, portal, messages, project-overview)
 * render outside any group and need no group expansion.
 */
const SECTION_TO_GROUP: Record<string, string> = {
  scopes: 'group-project-management',
  deliverables: 'group-project-management',
  team: 'group-project-management',
  permits: 'group-project-management',
  'billable-items': 'group-financials',
  sov: 'group-financials',
  'site-visits': 'group-construction-admin',
  submittals: 'group-construction-admin',
  rfis: 'group-construction-admin',
  sis: 'group-construction-admin',
  'client-actions': 'group-construction-admin',
  documents: 'group-construction-admin',
};

/** Section group header with accent color, label, and collapse toggle */
interface SectionGroupHeaderProps {
  label: string;
  accentColor: string;
  isExpanded: boolean;
  onToggle: () => void;
}

function SectionGroupHeader({ label, accentColor, isExpanded, onToggle }: SectionGroupHeaderProps) {
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-2 w-full text-left py-2 group"
      aria-expanded={isExpanded}
    >
      <div className={cn('w-1.5 h-4 rounded-full', accentColor)} />
      <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground group-hover:text-foreground transition-colors">
        {label}
      </span>
      <ChevronDown className={cn('w-3 h-3 text-muted-foreground ml-1 transition-transform duration-200', !isExpanded && '-rotate-90')} />
    </button>
  );
}

/** Phase 10c.9: Per-project cover image upload (inline sub-component) */
function ProjectCoverImageUpload({ projectId }: { projectId: string }) {
  const { data: coverImage, isLoading } = useProjectCoverImage(projectId);
  const updateCoverImage = useUpdateProjectCoverImage();

  if (isLoading) return null;

  return (
    <ImageUpload
      value={coverImage ?? null}
      onChange={(value) => updateCoverImage.mutate({ projectId, coverImage: value })}
      label="Proposal Cover Image"
      description="Override the default cover image for this proposal."
      targetMaxWidth={1200}
      maxFileSize={500 * 1024}
      outputFormat="jpeg"
      quality={0.85}
      testId="project-cover-image"
    />
  );
}

/**
 * Extracted outside ProjectDetailPage with LOCAL editing state.
 *
 * Why: typing in a controlled textarea that pushes state to the parent
 * (ProjectDetailPage with ~30 useState hooks + React Query subscriptions)
 * causes a heavy re-render on every keystroke. The re-render is slow enough
 * that the browser resets the cursor to position 0 before the next keystroke,
 * making text appear reversed ("typing backwards").
 *
 * Fix: the textarea value lives HERE, so keystrokes only re-render this small
 * component. The parent value is only read on enter-edit and written on save.
 */
interface ProjectUnderstandingEditProps {
  projectId: string;
  projectUnderstanding: string | null | undefined;
  onSave: (projectId: string, value: string | null) => void;
}

function ProjectUnderstandingEdit({
  projectId,
  projectUnderstanding,
  onSave,
}: ProjectUnderstandingEditProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [initialValue, setInitialValue] = useState('');

  const enterEdit = useCallback(() => {
    const currentValue = projectUnderstanding || '';
    setValue(currentValue);
    setInitialValue(currentValue);
    setIsEditing(true);
  }, [projectUnderstanding]);

  const handleSave = useCallback(() => {
    if (value !== initialValue) {
      onSave(projectId, value || null);
    }
    setIsEditing(false);
  }, [value, initialValue, onSave, projectId]);

  const handleCancel = useCallback(() => {
    setValue(initialValue);
    setIsEditing(false);
  }, [initialValue]);

  return isEditing ? (
    <div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        rows={6}
        className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        placeholder="Describe your understanding of the project scope and context..."
        autoFocus
      />
      <InlineEditActions
        onSave={handleSave}
        onCancel={handleCancel}
        showEnterHint={false}
        showSaveButton={true}
      />
    </div>
  ) : (
    <div className="relative group">
      <div
        className="cursor-pointer hover:ring-1 hover:ring-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 rounded px-2 py-1 pr-8 transition-all duration-200 min-h-[60px]"
        role="button"
        tabIndex={0}
        onClick={enterEdit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            enterEdit();
          }
        }}
        title="Click to edit project understanding"
        data-testid="project-understanding-display"
      >
        {projectUnderstanding ? (
          <p className="text-sm whitespace-pre-wrap">{projectUnderstanding}</p>
        ) : (
          <p className="text-sm text-muted-foreground italic">Click to add project understanding...</p>
        )}
      </div>
      <Pencil
        aria-hidden
        className="absolute top-2 right-2 h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors"
      />
    </div>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // 9.15c: Get back URL with stored page
  const { getBackToListUrl } = useReferrerPage('projects');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<BillableItem | null>(null);
  const [showAddScopeModal, setShowAddScopeModal] = useState(false); // Phase 5 Batch 3
  const [addScopeDefaultType, setAddScopeDefaultType] = useState<'client' | null>(null);
  const [isEditingWorkflowStage, setIsEditingWorkflowStage] = useState(false); // Phase 6 Batch 5.2: Inline editing
  const [isEditingAddress, setIsEditingAddress] = useState(false); // Phase 6 Batch 5.2: Inline editing
  const [addressValue, setAddressValue] = useState('');
  const [initialAddressValue, setInitialAddressValue] = useState(''); // Store initial value for comparison
  const [reviewIntent, setReviewIntent] = useState<InvoiceChangeIntent | null>(null);
  const siteSaveStarted = useRef(false);
  const [isEditingName, setIsEditingName] = useState(false); // Phase 6 Batch 5.3: Project name inline editing
  const [nameValue, setNameValue] = useState('');
  const [initialNameValue, setInitialNameValue] = useState('');
  const [isEditingNotes, setIsEditingNotes] = useState(false); // Phase 6 Batch 5.3: Project notes inline editing
  const [notesValue, setNotesValue] = useState('');
  const [initialNotesValue, setInitialNotesValue] = useState('');
  // 9.9d CONTRACT-03: Fee assumptions state removed - FeeAssumptionsList handles its own state
  // Phase 8.3.7: Project understanding inline editing
  // State moved into ProjectUnderstandingEdit component to prevent typing-backwards bug
  // (parent re-render was resetting cursor position on every keystroke)
  // Phase 9.5f: Status field removed - workflow_stage is now the primary state indicator
  // 9.7a: Client inline editing state removed - client now editable via ScopeCard in scopes list
  const [expandedScopeId, setExpandedScopeId] = useState<string | null>(null); // Phase 6 Batch 2: Scope accordion
  const [isCreatingNewItem, setIsCreatingNewItem] = useState(false); // Phase 7 Batch 6: Inline billable item creation
  const [showConvertModal, setShowConvertModal] = useState(false); // Phase 7 Batch 7.3: Convert proposal to project
  const [showConvertToTemplateModal, setShowConvertToTemplateModal] = useState(false); // Phase 7.3.3 Phase 2.2: Convert project to template
  const [showCopyTemplateModal, setShowCopyTemplateModal] = useState(false); // Phase 7.3.3 Phase 2.6: Copy template to create new template
  const [isEditingCategory, setIsEditingCategory] = useState(false); // Phase 7.5: Category inline editing
  // showEditDialog removed - Edit Details button removed, all fields are inline-editable
  // Action bar consolidation: Controlled send dialog for overflow menu
  const [sendProposalDialogOpen, setSendProposalDialogOpen] = useState(false);
  const [sendProposalDialogInitialTab, setSendProposalDialogInitialTab] = useState<'edit' | 'preview' | 'pdf' | 'history'>('edit');
  // Phase 10d.3: Share with Client dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  // planTEK: Messages slide-over (header icon button)
  const [messagesSlideOverOpen, setMessagesSlideOverOpen] = useState(false);

  // Phase 9.6b: Project number inline editing
  const [isEditingProjectNumber, setIsEditingProjectNumber] = useState(false);
  const [projectNumberValue, setProjectNumberValue] = useState('');
  const [initialProjectNumberValue, setInitialProjectNumberValue] = useState('');

  // R8: Auto-open Add Scope modal (client preset) when arriving via ?action=add-client-scope
  // Used by InvoiceCreatePage's dead-end CTA so Christa lands ready to add a client.
  const actionParam = searchParams.get('action');
  useEffect(() => {
    if (actionParam === 'add-client-scope') {
      setAddScopeDefaultType('client');
      setShowAddScopeModal(true);
    }
  }, [actionParam]);

  // 9.15d: Context-aware section state
  // R2: Canonical section keys replace hardcoded validSections array.
  const sectionParam = searchParams.get('section');
  const initialOpenSection =
    (CORE_SECTIONS as readonly string[]).includes(sectionParam ?? '') ||
    (ADDON_SECTIONS as readonly string[]).includes(sectionParam ?? '')
      ? sectionParam
      : null;

  // v0.17: A `?section=` deep link must also open the section's containing
  // GROUP (Project Management / Financials / Construction Admin) in the
  // grouped layout — otherwise the section renders "open" inside a collapsed
  // group and never appears in the DOM. This is a VIEW-ONLY override, not
  // persisted via setGroupExpanded: a one-time link click (an email
  // notification, a bookmark, an e2e test) shouldn't silently overwrite the
  // user's stored per-project group preference. Computed once at mount (lazy
  // initializer) so it doesn't re-fire on every render, and cleared the
  // moment the user manually toggles that same group (see toggleGroup below)
  // so it never fights a later explicit collapse.
  const [deepLinkGroupOverride, setDeepLinkGroupOverride] = useState<string | null>(
    () => (initialOpenSection && SECTION_TO_GROUP[initialOpenSection]) || null
  );

  // 9.15d: Track which sections are open (all collapsed by default unless section param)
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    scopes: initialOpenSection === 'scopes',
    'billable-items': initialOpenSection === 'billable-items',
    invoices: initialOpenSection === 'invoices',
    permits: initialOpenSection === 'permits',
    deliverables: initialOpenSection === 'deliverables',
    submittals: initialOpenSection === 'submittals',
    siteVisits: initialOpenSection === 'site-visits',
    rfis: initialOpenSection === 'rfis',
    sis: initialOpenSection === 'sis',
    messages: initialOpenSection === 'messages',
    team: initialOpenSection === 'team',
    clientActions: initialOpenSection === 'client-actions',
    sov: initialOpenSection === 'sov',
    portal: initialOpenSection === 'portal',
    documents: initialOpenSection === 'documents',
  });

  // Track sections that have been opened at least once (for lazy-loading queries)
  const [fetchedSections, setFetchedSections] = useState<Record<string, boolean>>(() => {
    // Pre-mark sections that start open (from URL param)
    const initial: Record<string, boolean> = {};
    if (initialOpenSection) initial[initialOpenSection] = true;
    return initial;
  });

  // 9.15d: Handler for section open/close
  const handleSectionChange = (section: string) => (open: boolean) => {
    setOpenSections(prev => ({ ...prev, [section]: open }));
    if (open) setFetchedSections(prev => ({ ...prev, [section]: true }));
  };

  // R3 + v0.15: Hide a section with override hygiene + snapshot-restore Undo.
  // Captures the overrides map AS IT WAS (snapshot), computes the next map via
  // the clear/set rule (so the override only exists when it differs from the
  // default stack), PATCHes it, and offers Undo that PATCHes the exact snapshot
  // back — restoring byte-identical state, including keys that were ABSENT.
  async function executeRemoveSection(sectionKey: string) {
    if (!project) return;
    const label = SECTION_METADATA[sectionKey]?.label ?? sectionKey;
    const snapshot = readOverrides(project);
    const nextOverrides = computeNextOverrides(snapshot, sectionKey, false, defaultStackVisibility);
    try {
      await sectionConfigMutation.mutateAsync({ project, overrides: nextOverrides });
      toast.success(`${label} hidden from this project`, {
        duration: 8000,
        action: {
          label: 'Undo',
          onClick: () => {
            sectionConfigMutation.mutate({ project, overrides: snapshot });
          },
        },
      });
    } catch {
      toast.error('Failed to hide section. Please try again.');
    }
  }

  // v0.15: Show a hidden section (override hygiene) and expand it. Used by the
  // hidden-section deep-link banner's "Show section" button.
  async function showSection(sectionKey: string) {
    if (!project) return;
    const snapshot = readOverrides(project);
    const nextOverrides = computeNextOverrides(snapshot, sectionKey, true, defaultStackVisibility);
    try {
      await sectionConfigMutation.mutateAsync({ project, overrides: nextOverrides });
      // openSections keys are camelCase for a couple of sections (site-visits → siteVisits).
      handleSectionChange(SECTION_OPEN_KEY[sectionKey] ?? sectionKey)(true);
    } catch {
      toast.error('Failed to show section. Please try again.');
    }
  }

  const { user } = useAuth(); // Phase 10d.3
  const hasPortalManagement = useFeature('portal_management'); // planTEK: reads from feature flags API (not JWT modules)

  // Phase 10d.3: Fetch portal member emails for "Has Portal Access" badge on scope contacts
  const { data: projectShareData } = useQuery({
    queryKey: ['project-share', projectId],
    queryFn: () => api.projectShare.getForProject(projectId),
    enabled: hasPortalManagement && Boolean(projectId),
    staleTime: 30_000,
  });
  const portalMemberEmails = useMemo(() => new Set<string>(
    (projectShareData?.members ?? [])
      .filter((m: any) => m.status !== 'revoked')
      .map((m: any) => String(m.member_email).toLowerCase())
  ), [projectShareData?.members]);

  // Auto-expand portal section when a portal already exists for this project
  useEffect(() => {
    if (hasPortalManagement && projectShareData?.view) {
      setOpenSections(prev => {
        if (prev.portal) return prev; // already open, don't interfere
        return { ...prev, portal: true };
      });
      setFetchedSections(prev => ({ ...prev, portal: true }));
    }
  }, [hasPortalManagement, projectShareData?.view]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: projectData, isLoading: projectLoading, error: projectError } = useProjectById(projectId);
  // R2: Hoist project + visibleSections so they're in scope for the overdueAcceptanceItems useMemo below.
  // Without this hoist, deliverable items still leak into ActionSummaryWidget when Deliverables is disabled per-project.
  const project = projectData?.project;
  const { visibleSections, hiddenAddIns, defaultStackVisibility, hardGateHidden } = useVisibleSections(project);
  const { data: billableItemsData, isLoading: itemsLoading, error: itemsError } = useBillableItems({
    project_id: projectData?.project?.id,
  });

  // Phase 5 Batch 3: Project scopes
  const { data: scopesData, isLoading: scopesLoading } = useProjectScopes(projectData?.project?.id);
  const updateScope = useUpdateProjectScope();
  const deleteScope = useDeleteProjectScope();
  // Phase 9.7bc: Multi-contact management
  const addScopeContact = useAddScopeContact();
  const removeScopeContact = useRemoveScopeContact();
  const setPrimaryScopeContact = useSetPrimaryScopeContact();
  const setInvoiceScopeContact = useSetInvoiceScopeContact();

  // Phase 7 Batch 6: Hoist permits data for ActionSummaryWidget and PermitsList
  const { data: permitsData = [] } = usePermits(projectData?.project?.id || '');

  // Phase 10e: Fetch client actions eagerly for the acceptance overdue widget
  // (non-templates only; uses portal_management gate since acceptance only applies when portal exists)
  const clientActionsProjectId = (!projectData?.project?.is_template && hasPortalManagement)
    ? projectData?.project?.id
    : undefined;
  const { data: clientActionsData } = useClientActions(clientActionsProjectId);

  // Lazy-loaded section queries: only fetch when section has been opened.
  // Helper: returns projectId only if the section has been opened
  const lazyId = (section: string) =>
    fetchedSections[section] ? projectData?.project?.id : undefined;

  // Phase 10b.7: Fetch deliverables (deferred until section opened)
  // NOTE: Must be declared before overdueAcceptanceItems useMemo which references it
  const { data: deliverablesData } = useDeliverables(lazyId('deliverables'));
  // Proposal compression summary: count fee assumptions (cached — FeeAssumptionsList child uses same key)
  const { data: feeAssumptionsData } = useProjectFeeAssumptions(projectData?.project?.id);
  const deliverablesCount = deliverablesData?.length || 0;

  // Phase 10e: Compute overdue acceptance items for ActionSummaryWidget
  // An item is "overdue" if accepted_at IS NULL and created_at > threshold ago.
  const { data: operationalSettings } = useOperationalSettings();
  const overdueHours = operationalSettings?.acceptance_reminder_hours ?? 48;
  const { data: proposalDefaultsData } = useProposalDefaults();
  const overdueAcceptanceItems = useMemo((): OverdueAcceptanceItem[] => {
    const now = Date.now();
    const thresholdMs = overdueHours * 3_600_000;
    const items: OverdueAcceptanceItem[] = [];

    // Client actions
    const actions = clientActionsData?.actions ?? [];
    for (const action of actions) {
      if (action.accepted_at === undefined) continue; // pre-V118
      if (action.accepted_at) continue; // already accepted
      if (!action.created_at) continue;
      const ageMs = now - new Date(action.created_at).getTime();
      if (ageMs > thresholdMs) {
        items.push({
          id: action.id,
          name: action.title,
          itemType: 'client_action',
          assignedTo: action.completed_by_email ?? null,
          createdAt: action.created_at,
        });
      }
    }

    // Deliverables (only available after section has been opened).
    // R2 follow-up: gate on visibleSections so overdue deliverables don't leak into the widget
    // on projects where Deliverables has been hidden per-project (or via category default / module visibility).
    // The lazy-load partly mitigates this — deliverablesData is undefined until the section is opened —
    // but a user who opens Deliverables, then removes it via the kebab without reloading, would still leak.
    if (visibleSections.includes('deliverables')) for (const deliverable of (deliverablesData || [])) {
      if (deliverable.accepted_at === undefined) continue; // pre-V118
      if (deliverable.accepted_at) continue; // already accepted
      if (!deliverable.created_at) continue;
      const ageMs = now - new Date(deliverable.created_at).getTime();
      if (ageMs > thresholdMs) {
        items.push({
          id: deliverable.id,
          name: deliverable.title,
          itemType: 'deliverable',
          assignedTo: deliverable.assigned_to,
          createdAt: deliverable.created_at,
        });
      }
    }

    // Sort by oldest first (most overdue at top)
    items.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return items;
  }, [clientActionsData, deliverablesData, overdueHours, visibleSections]);

  // Client actions count (data already fetched eagerly for overdue widget)
  const clientActionsCount = clientActionsData?.actions?.length || 0;

  // Issue #95: Eager fetch counts for collapsed-header badges (Construction Admin
  // sections). Templates skip — they never carry these records. Same query key as
  // the in-section hooks below, so the cache is reused on expand (no duplicate
  // request). On fetch error, data is undefined → count is 0 → badge silently
  // omitted (header reads "Submittals" not "Submittals (—)").
  const headerCountProjectId = (!projectData?.project?.is_template)
    ? projectData?.project?.id
    : undefined;

  // Submittals — eager (badge count) + reused on expand
  const { data: submittalsData } = useSubmittals(headerCountProjectId);
  const submittalsCount = submittalsData?.length || 0;
  const { data: siteVisitsData } = useSiteVisits(headerCountProjectId); // Phase 10d.1
  const siteVisitsCount = siteVisitsData?.length || 0;
  const { data: rfisData } = useRFIs(headerCountProjectId); // Phase 10d.2
  const rfisCount = rfisData?.rfis?.length || 0;
  const { data: sisData } = useSIs(headerCountProjectId); // R3: Supplemental Instructions
  const sisCount = sisData?.sis?.length || 0;
  const { data: messagesData } = useProjectMessages(lazyId('messages')); // planTEK: message thread
  const messagesCount = messagesData?.messages?.length || 0;
  // planTEK: Eager fetch for header badge (non-templates only, so templates don't trigger extra request)
  const headerMessagesProjectId = (!projectData?.project?.is_template) ? projectData?.project?.id : undefined;
  const { data: headerMessagesData } = useProjectMessages(headerMessagesProjectId);
  const unreadMessagesCount = (headerMessagesData?.messages ?? [])
    .filter(msg => !msg.read_by.includes(user?.user_id ?? '')).length;

  // Phase 10c.10: Fetch project members (deferred until section opened)
  const { data: projectMembersData } = useProjectMembers(lazyId('team'));
  const projectMembersCount = projectMembersData?.length || 0;

  // R3: Section data counts for remove-confirmation threshold (count > 0 → show confirm dialog).
  // Sections without a readily-available count (sov, portal) default to 0 (no confirmation).
  const sectionDataCounts: Record<string, number> = {
    deliverables: deliverablesCount,
    submittals: submittalsCount,
    rfis: rfisCount,
    sis: sisCount,
    'site-visits': siteVisitsCount,
    team: projectMembersCount,
    messages: messagesCount,
    sov: 0,
    portal: 0,
  };

  // 9.5c: Fetch invoices for summary stats
  const { data: invoicesData } = useInvoices(
    { project_id: projectData?.project?.id },
    { keepPreviousData: true }
  );
  const invoices = invoicesData?.invoices || [];

  // Issue #13: Full scope editing - fetch organizations, scope types, and contacts
  const { data: organizationsData } = useOrganizations();
  const { data: scopeTypesData } = useScopeTypes();
  const [selectedOrgIdForContacts, setSelectedOrgIdForContacts] = useState<string | undefined>();
  const { data: contactsData } = useOrganizationContacts(selectedOrgIdForContacts);

  // Phase 7.5: Fetch project categories for inline editing
  const { data: projectCategoryOptions } = useProjectCategoryOptions();

  const deleteProject = useDeleteProject();
  const restoreProject = useRestoreProject();
  const deleteBillableItem = useDeleteBillableItem();
  const convertToTemplate = useConvertToTemplate(); // Phase 7.3.3 Phase 2: Convert project to template
  const copyTemplate = useCopyTemplate(); // Phase 7.3.3 Phase 2.6: Copy template
  const sendProposal = useSendProposal(); // Phase 7.5.10: Mark proposal as sent
  const declineProposal = useDeclineProposal(); // Phase 9.6: Mark proposal as lost
  const updateProjectInline = useUpdateProjectInline(); // Phase 6 Batch 5.2: Inline editing

  // R3: Mutation hook for add/remove section_config overrides.
  // (project + useVisibleSections hoisted earlier to be in scope for overdueAcceptanceItems useMemo.)
  const sectionConfigMutation = useSectionConfig();
  // R3: canEdit — project-edit roles only (admin/user); viewers and externals are read-only.
  const canEdit = !!user && user.role !== 'viewer' && user.role !== 'external';
  // R3: Filter portal from picker when portal_management feature flag is off (hard-gated).
  const pickableHiddenAddIns = hiddenAddIns.filter(
    (s) => s !== 'portal' || hasPortalManagement,
  );
  // R3: State for "remove section" confirmation dialog.
  const [confirmRemoveSection, setConfirmRemoveSection] = useState<{ key: string; label: string } | null>(null);
  // v0.15: Deep-link banner — when ?section= targets an add-in that resolved HIDDEN,
  // explain why and (unless hard-gated) offer a one-click "Show section".
  const hiddenDeepLinkSection =
    sectionParam &&
    (ADDON_SECTIONS as readonly string[]).includes(sectionParam) &&
    !visibleSections.includes(sectionParam) &&
    SECTION_METADATA[sectionParam]
      ? {
          key: sectionParam,
          label: SECTION_METADATA[sectionParam].label,
          hardGated: !!hardGateHidden[sectionParam as keyof typeof hardGateHidden],
        }
      : null;
  const { isGroupExpanded, setGroupExpanded } = useProjectSectionPreferences(project?.id, project?.workflow_stage);
  const firmType = useFirmType();
  // Show grouped layout when firm_type is set; degrade to flat when null/default
  const showGrouped = firmType !== 'default';
  // Effective expanded state for a group: stored/auto-default prefs OR'd with
  // the one-time deep-link override computed above.
  const isGroupOpen = (groupKey: string) =>
    isGroupExpanded(groupKey) || deepLinkGroupOverride === groupKey;
  // Toggling a group always writes through to stored prefs (setGroupExpanded);
  // it also drops the deep-link override for that group so a manual collapse
  // right after landing on a deep link actually sticks.
  const toggleGroup = (groupKey: string) => {
    setGroupExpanded(groupKey, !isGroupOpen(groupKey));
    if (deepLinkGroupOverride === groupKey) setDeepLinkGroupOverride(null);
  };
  const billableItems = billableItemsData?.billable_items || [];
  const scopes = scopesData?.scopes || []; // Phase 5 Batch 3
  // 9.7a: clientScope extraction removed - client scope now displayed in scopes list



  // 9.15d: Scroll to opened section after initial load
  useEffect(() => {
    if (initialOpenSection && !projectLoading) {
      const sectionId = `${initialOpenSection}-section`;
      const element = document.getElementById(sectionId);
      if (element) {
        // Small delay to let section expand animation complete
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [initialOpenSection, projectLoading]);

  // Issue #13: Extract data arrays for full scope editing
  const organizations = organizationsData?.organizations ?? [];
  const scopeTypes = scopeTypesData ?? [];
  const contacts = contactsData?.contacts ?? [];

  // Phase 5 Batch 3: Calculate action summary from scopes
  const waitingOnUsScopes = scopes.filter(s => s.status === 'waiting_on_us');
  const waitingOnThemScopes = scopes.filter(s => s.status === 'waiting_on_them');

  // F6: Roll customer-side client_actions into the same "Waiting on" counts so
  // the widget reflects portal activity, not just project scopes. Heuristic:
  //   - status pending|in_progress (or completed but not yet accepted by admin)
  //     with action_type the customer drives -> Waiting on Them
  //   - status completed but accepted_at is null and the action carries an
  //     attached document_review_id -> Waiting on Us (admin must close the loop)
  // Skipped/already-accepted actions don't count.
  const clientActionsForCounts = clientActionsData?.actions ?? [];
  const waitingOnThemActions = clientActionsForCounts.filter((a: any) => {
    if (!a || a.status === 'skipped') return false;
    return a.status === 'pending' || a.status === 'in_progress';
  });
  const waitingOnUsActions = clientActionsForCounts.filter((a: any) => {
    if (!a || a.status !== 'completed') return false;
    // If the customer marked it done but the admin hasn't accepted (V118
    // acceptance tracking), it's our turn to close the loop.
    return a.accepted_at === null || a.accepted_at === undefined;
  });

  const actionSummary = {
    waitingOnUsCount: waitingOnUsScopes.length + waitingOnUsActions.length,
    waitingOnThemCount: waitingOnThemScopes.length + waitingOnThemActions.length,
    scopesWithActionNeeded: waitingOnUsScopes.map(s => ({
      id: s.id,
      organization_name: s.organization_name,
      scope_type_name: s.scope_type || 'Unknown',
      status: s.status || '',
      status_notes: s.status_notes || null,
    })),
  };


  const formatCurrency = (value: number | undefined) => {
    if (value === undefined || value === null) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Summary helpers for CollapsibleSection headers
  // Phase 9.5f: Removed status badge from overview summary - workflow_stage is now the primary indicator
  const getOverviewSummary = () => (
    <div className="flex items-center gap-3 text-sm">
      {project?.workflow_stage && !project?.is_template && (
        <ProjectStageBadge
          workflowStage={project.workflow_stage as WorkflowStage}
          updatedAt={project.updated_at}
        />
      )}
      {project?.is_template && (
        <Badge variant="secondary">Template</Badge>
      )}
      <span className="text-gray-500">•</span>
      <span className="text-gray-700">{project?.client_name || 'No client'}</span>
      {project?.address && (
        <>
          <span className="text-gray-500">•</span>
          <span className="text-gray-600 truncate max-w-[200px]">{project.address}</span>
        </>
      )}
    </div>
  );

  // 9.7a: Sort scopes with client scope first
  const sortedScopes = useMemo(() => {
    return [...scopes].sort((a, b) => {
      // Client scope always first
      if (a.is_client_type && !b.is_client_type) return -1;
      if (!a.is_client_type && b.is_client_type) return 1;
      return 0; // Preserve existing order for non-client scopes
    });
  }, [scopes]);

  const getScopesSummary = () => {
    // 9.7a: Include all scopes in summary (client scope is now in the list)
    const waitingOnUs = scopes.filter(s => s.status === 'waiting_on_us').length;
    const waitingOnThem = scopes.filter(s => s.status === 'waiting_on_them').length;
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="text-gray-700">{scopes.length} scopes</span>
        {waitingOnUs > 0 && (
          <span className="text-orange-600 font-medium">{waitingOnUs} waiting on us</span>
        )}
        {waitingOnThem > 0 && (
          <span className="text-sky-600 font-medium">{waitingOnThem} waiting on them</span>
        )}
      </div>
    );
  };

  const getBillableItemsSummary = () => {
    // v0.14e: Exclude cancelled items from rollup totals/counts
    // Cancelled items are visible in the table but excluded from financial summary
    const { count, total } = summarizeBillableItemsForSection(billableItems);
    const unbilled = project?.unbilled_total || 0;
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="text-gray-700">{count} items</span>
        <span className="text-gray-700">${total.toLocaleString()} total</span>
        {unbilled > 0 && (
          <span className="text-orange-600 font-medium">${unbilled.toLocaleString()} unbilled</span>
        )}
      </div>
    );
  };

  const getPermitsSummary = () => {
    const now = new Date();
    const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    const expiringSoon = permitsData.filter(p => {
      if (!p.expiration_date) return false;
      const expDate = new Date(p.expiration_date);
      return expDate <= thirtyDaysFromNow && expDate >= now;
    }).length;
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="text-gray-700">{permitsData.length} {permitsData.length === 1 ? 'permit' : 'permits'}</span>
        {expiringSoon > 0 && (
          <span className="text-orange-600 font-medium">{expiringSoon} expiring soon</span>
        )}
      </div>
    );
  };

  const getInvoicesSummary = () => {
    const outstanding = project?.unpaid_fee || 0;
    const overdueCount = invoices.filter(i => i.display_status === 'overdue').length;
    return (
      <div className="flex items-center gap-3 text-sm">
        <span className="text-gray-700">{invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}</span>
        {outstanding > 0 && (
          <span className="text-gray-700">${outstanding.toLocaleString()} outstanding</span>
        )}
        {overdueCount > 0 && (
          <span className="text-red-600 font-medium">{overdueCount} overdue</span>
        )}
      </div>
    );
  };

  // R3: Returns a kebab menu for an add-in section header. canEdit-gated.
  const makeSectionKebab = (sectionKey: string) => {
    if (!canEdit) return undefined;
    const label = SECTION_METADATA[sectionKey]?.label ?? sectionKey;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="p-1 rounded hover:bg-muted transition-colors"
            aria-label="Section options"
            title="Section options"
            data-testid={`section-kebab-${sectionKey}`}
          >
            <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => {
              const hasData = (sectionDataCounts[sectionKey] ?? 0) > 0;
              if (hasData) {
                setConfirmRemoveSection({ key: sectionKey, label });
              } else {
                executeRemoveSection(sectionKey);
              }
            }}
            data-testid={`section-remove-${sectionKey}`}
          >
            Hide this section
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const handleDelete = async () => {
    if (!project || !project.id) return;
    try {
      await deleteProject.mutateAsync(project.id);
      navigate('/projects');
    } catch (error) {
      setDeleteDialogOpen(false);
    }
  };

  const handleRestore = async () => {
    if (!project || !project.id) return;
    try {
      // #150: pass the entity label so the toast reads "Proposal restored" etc.
      const entityLabel = project.is_template
        ? 'Template'
        : isProposalStage(project.workflow_stage) ? 'Proposal' : 'Project';
      await restoreProject.mutateAsync({ id: project.id, entityLabel });
    } catch (error) {
      // Toast handled by hook
    }
  };

  // Phase 7.3.3 Phase 2: Convert project to template
  const handleConvertToTemplate = async () => {
    if (!project?.id) return;
    setShowConvertToTemplateModal(false);
    try {
      const response = await convertToTemplate.mutateAsync(project.id);
      // Navigate to the new template
      if (response?.template?.id) {
        navigate(`/projects/${response.template.id}`);
      }
    } catch (error) {
      // Error toast already shown by mutation hook
    }
  };

  const handleCopyTemplate = () => {
    if (!project?.id) return;
    setShowCopyTemplateModal(false);
    copyTemplate.mutate(project.id);
  };

  // Bug #12 fix: Handle inline delete for billable items
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    try {
      await deleteBillableItem.mutateAsync(itemToDelete.id);
      setItemToDelete(null);
    } catch (error) {
      setItemToDelete(null);
    }
  };

  // Phase 5 Batch 3: Scope handlers
  const handleUpdateScopeStatus = (scopeId: string, newStatus: ScopeStatus) => {
    if (!project?.id) return;

    updateScope.mutate({
      id: scopeId,
      projectId: project.id,
      data: { status: newStatus },
    });
  };

  const handleDeleteScope = (scopeId: string) => {
    if (!project?.id) return;

    deleteScope.mutate({
      id: scopeId,
      projectId: project.id,
    });
  };

  // Phase 9.7bc: Set primary client scope handler
  const handleSetPrimary = (scopeId: string) => {
    if (!project?.id) return;
    const scope = scopes.find((item) => item.id === scopeId);
    if (!scope?.organization_id || !scope.is_client_type) return;
    setReviewIntent({ intent: 'project_assignment', organization_id: scope.organization_id,
      target_organization_id: scope.organization_id, project_ids: [project.id],
      client_scope_ids: [scope.id], target_establishment_id: scope.establishment_id ?? null,
      future_defaults: true });
  };

  // Phase 9.7bc: Multi-contact management handlers
  const handleAddContact = (scopeId: string, contactId: string) => {
    if (!project?.id) return;
    addScopeContact.mutate({
      projectId: project.id,
      scopeId,
      contactId,
    });
  };

  const handleRemoveContact = (scopeId: string, contactId: string) => {
    if (!project?.id) return;
    removeScopeContact.mutate({
      projectId: project.id,
      scopeId,
      contactId,
    });
  };

  const handleSetPrimaryContact = (scopeId: string, contactId: string) => {
    if (!project?.id) return;
    setPrimaryScopeContact.mutate({
      projectId: project.id,
      scopeId,
      contactId,
    });
  };

  const handleSetInvoiceContact = (scopeId: string, contactId: string) => {
    if (!project?.id) return;
    setInvoiceScopeContact.mutate({
      projectId: project.id,
      scopeId,
      contactId,
    });
  };

  // Issue #13: Full scope update handler
  const handleUpdateScope = (data: Partial<typeof scopes[0]> & { id: string }) => {
    if (!project?.id) return;
    const prior = scopes.find((item) => item.id === data.id);
    if (prior?.is_client_type && prior.organization_id && data.organization_id
        && data.organization_id !== prior.organization_id) {
      setReviewIntent({ intent: 'project_assignment', organization_id: prior.organization_id,
        target_organization_id: data.organization_id, project_ids: [project.id],
        client_scope_ids: [prior.id], target_establishment_id: data.establishment_id ?? null,
        future_defaults: true });
      return;
    }
    if (prior?.is_client_type && prior.organization_id && data.establishment_id !== undefined
        && data.establishment_id !== (prior.establishment_id ?? null)) {
      setReviewIntent({ intent: 'identity_default', organization_id: prior.organization_id,
        future_defaults: true, default_level: 'this_project', default_field: 'dba',
        project_ids: [project.id], client_scope_ids: [prior.id],
        default_establishment_id: data.establishment_id });
      return;
    }

    updateScope.mutate({
      id: data.id,
      projectId: project.id,
      data: buildScopeUpdatePayload(data),
    });
  };

  const saveSiteAddress = () => {
    if (!project || addressValue === initialAddressValue || siteSaveStarted.current) return;
    siteSaveStarted.current = true;
    if (project.billing_address) {
      updateProjectInline.mutate({ projectId: project.id, data: { address: addressValue || null } });
    } else {
      setReviewIntent({ intent: 'project_address_correction', project_ids: [project.id],
        project_address_field: 'site_address', fields: { billing_address: addressValue },
        future_defaults: true });
    }
    setIsEditingAddress(false);
  };

  // Issue #13: Handler for organization change to fetch contacts
  const handleOrganizationChange = (organizationId: string) => {
    setSelectedOrgIdForContacts(organizationId);
  };

  // ProjectUnderstandingEdit extracted to module scope with LOCAL state to prevent
  // typing-backwards bug (parent re-render reset cursor to position 0 on every keystroke).
  // IMPORTANT: This useCallback MUST be before any early returns to obey Rules of Hooks.
  const handleProjectUnderstandingSave = useCallback((pid: string, value: string | null) => {
    updateProjectInline.mutate({ projectId: pid, data: { project_understanding: value } });
  }, [updateProjectInline]);

  if (projectLoading) {
    return (
      <DashboardLayout title="Project Details">
        <div className="space-y-6">
          <Skeleton className="h-12 w-full max-w-2xl" />
          <div className="grid gap-6 md:grid-cols-3">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
          <Skeleton className="h-96" />
        </div>
      </DashboardLayout>
    );
  }

  if (projectError || !project) {
    return (
      <DashboardLayout title="Project Details">
        <div className="rounded-lg border border-destructive bg-destructive/10 p-4">
          <p className="text-sm text-destructive">
            Failed to load project: {(projectError as Error)?.message || 'Project not found'}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  // Phase 7.3.3 Phase 5: Context-aware navigation
  // Phase 8.5: Add proposal-aware navigation
  const returnTo = searchParams.get('returnTo');
  const shouldReturnToTemplates = returnTo === 'templates' || project.is_template;
  const shouldReturnToProposals = returnTo === 'proposals' || (!project.is_template && isProposalStage(project.workflow_stage));

  // Determine back navigation destination
  // 9.15c: Use stored page for projects list navigation
  const getBackDestination = () => {
    if (shouldReturnToTemplates) return '/settings?tab=templates';
    if (shouldReturnToProposals) return '/projects?view=proposals';
    return getBackToListUrl(); // 9.15c: Returns /projects?page=N if stored
  };

  const getBackLabel = () => {
    if (shouldReturnToTemplates) return 'Back to Templates';
    if (shouldReturnToProposals) return 'Back to Proposals';
    return 'Back to Projects';
  };

  return (
    <DashboardLayout
      title={project.name}
      actions={
        <>
          {/* Back button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(getBackDestination())}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {getBackLabel()}
          </Button>
          {/* State-driven primary CTA */}
          {/* Unsent proposals: Send Proposal is primary */}
          {isProposalStage(project.workflow_stage) && !project.is_template && !project.proposal_sent_at && (
            <SendProposalButton
              projectId={project.id}
              proposalNumber={project.proposal_number || project.project_number}
              projectName={project.name}
              defaultRecipient={scopes.find(s => s.is_client_type)?.contact_email || ''}
              disabled={billableItems.filter(item => ['proposed', 'proposed-optional'].includes(item.item_status) && !item.cancelled).length === 0}
            />
          )}
          {/* Sent proposals: Convert to Project is primary */}
          {isProposalStage(project.workflow_stage) && !project.is_template && project.proposal_sent_at && (
            <Button
              onClick={() => setShowConvertModal(true)}
              variant="default"
              size="sm"
              data-testid="convert-proposal-button"
            >
              Convert to Project
            </Button>
          )}
          {/* Templates: Create from Template is primary */}
          {project.is_template && (
            <Button
              onClick={() => navigate(`/projects/new?fromTemplate=${project.id}`)}
              variant="default"
              size="sm"
            >
              <FileText className="mr-2 h-4 w-4" />
              Create from Template
            </Button>
          )}
          {/* Download PDF - proposals and converted projects */}
          {!project.is_template && (
            <ProposalPdfDownload
              projectId={project.id}
              projectName={project.name}
              documentPrefix={proposalDefaultsData?.document_prefix}
              hasProjectUnderstanding={!!project.project_understanding}
              hasFeeAssumptions={!!project.fee_assumptions}
              hasClientContact={!!scopes.find(s => s.is_client_type)?.contact_name}
              billableItems={billableItems}
            />
          )}
          {/* Messages icon button with unread badge */}
          {!project.is_template && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative"
                    aria-label="Open messages"
                    onClick={() => setMessagesSlideOverOpen(true)}
                  >
                    <MessageCircle className="h-4 w-4 text-violet-600" />
                    {unreadMessagesCount > 0 && (
                      <Badge
                        className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-[10px] bg-violet-600 text-white"
                      >
                        {unreadMessagesCount > 9 ? '9+' : unreadMessagesCount}
                      </Badge>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Messages — project-level thread with your team</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Overflow menu */}
          <EntityActionMenu
            items={[
              // Preview PDF - for proposals (not templates)
              ...(isProposalStage(project.workflow_stage) && !project.is_template ? [{
                label: 'Preview PDF',
                icon: Eye,
                onClick: () => {
                  setSendProposalDialogInitialTab('pdf');
                  setSendProposalDialogOpen(true);
                },
              }] : []),
              // Unsent proposals: Convert to Project in overflow
              ...(isProposalStage(project.workflow_stage) && !project.is_template && !project.proposal_sent_at ? [{
                label: 'Convert to Project',
                icon: FileText,
                onClick: () => setShowConvertModal(true),
              }] : []),
              // Sent proposals: Resend Proposal
              ...(isProposalStage(project.workflow_stage) && !project.is_template && project.proposal_sent_at ? [{
                label: 'Resend Proposal',
                icon: Mail,
                onClick: () => {
                  setSendProposalDialogInitialTab('edit');
                  setSendProposalDialogOpen(true);
                },
              }] : []),
              // Proposals: Mark as Sent (if not already sent)
              ...(isProposalStage(project.workflow_stage) && !project.is_template && !project.proposal_sent_at ? [{
                label: 'Mark as Sent',
                icon: Send,
                onClick: () => sendProposal.mutate(project.id),
                disabled: sendProposal.isPending,
              }] : []),
              // Proposals: Mark as Lost (if not already declined)
              ...(isProposalStage(project.workflow_stage) && !project.is_template && project.workflow_stage !== 'proposal_declined' ? [{
                label: 'Mark as Lost',
                icon: XCircle,
                onClick: () => declineProposal.mutate(project.id),
                disabled: declineProposal.isPending,
              }] : []),
              // Phase 10d.3: Share with Client (portal_management gate, non-templates only)
              ...(hasPortalManagement && !project.is_template ? [{
                label: 'Share with Client',
                icon: Users,
                onClick: () => setShareDialogOpen(true),
                separator: true,
              }] : []),
              // Non-templates: Save as Template
              ...(!project.is_template ? [{
                label: 'Save as Template',
                icon: FileStack,
                onClick: () => setShowConvertToTemplateModal(true),
                separator: !hasPortalManagement,
              }] : []),
              // Templates: Copy Template
              ...(project.is_template ? [{
                label: 'Copy Template',
                icon: Copy,
                onClick: () => setShowCopyTemplateModal(true),
              }] : []),
              // Archive/Restore - always available
              ...(project.is_archived ? [{
                label: 'Restore',
                icon: RotateCcw,
                onClick: handleRestore,
                disabled: restoreProject.isPending,
                separator: true,
              }] : [{
                label: 'Archive',
                icon: Archive,
                onClick: () => setDeleteDialogOpen(true),
                variant: 'destructive' as const,
                separator: true,
              }]),
            ] as ActionMenuItem[]}
          />
          {/* Hidden send dialog for resend/history from overflow menu */}
          {isProposalStage(project.workflow_stage) && !project.is_template && (
            <SendProposalButton
              projectId={project.id}
              proposalNumber={project.proposal_number || project.project_number}
              projectName={project.name}
              defaultRecipient={scopes.find(s => s.is_client_type)?.contact_email || ''}
              hideButton
              open={sendProposalDialogOpen}
              onOpenChange={setSendProposalDialogOpen}
              initialTab={sendProposalDialogInitialTab}
            />
          )}
          {/* Phase 10d.3: Share with Client dialog (portal_management gate) */}
          {hasPortalManagement && !project.is_template && (
            <ShareWithClientDialog
              open={shareDialogOpen}
              onOpenChange={setShareDialogOpen}
              projectId={project.id}
              projectName={project.name}
              scopeContacts={scopes.map(s => ({
                contact_email: s.contact_email,
                contact_name: s.contact_name,
                organization_name: s.organization_name,
                is_client_type: s.is_client_type,
              }))}
            />
          )}
          {/* planTEK: Messages slide-over */}
          {!project.is_template && (
            <MessagesSlideOver
              projectId={project?.id || ''}
              open={messagesSlideOverOpen}
              onOpenChange={setMessagesSlideOverOpen}
            />
          )}
        </>
      }
    >
      <div className="space-y-6">
      {/* V108: Archived project banner */}
      {project.is_archived && (
        <div className="rounded-lg border border-muted bg-muted/50 p-4 flex items-center justify-between" data-testid="archived-banner">
          <div className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">
              This {project.is_template ? 'template' : isProposalStage(project.workflow_stage) ? 'proposal' : 'project'} was archived{project.archived_at ? ` on ${new Date(project.archived_at).toLocaleDateString()}` : ''}.
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRestore}
            disabled={restoreProject.isPending}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {restoreProject.isPending ? 'Restoring...' : 'Restore'}
          </Button>
        </div>
      )}

      {/* R5: Orphan client banner (spec keystone-client-required-per-category) */}
      <OrphanClientBanner project={project} scopes={scopesData?.scopes} />

      {/* Phase 8.5: Proposal indicator banner */}
      {isProposalStage(project.workflow_stage) && !project.is_template && (
        <div className="flex items-center gap-2 mb-2" data-testid="proposal-banner">
          <Badge variant="default" className="bg-amber-500 hover:bg-amber-500 text-white">
            PROPOSAL
          </Badge>
          <span className="text-sm text-muted-foreground">
            This is a proposal — convert to project once accepted
          </span>
        </div>
      )}

      {/* Phase 6 Batch 5.3: Project name inline editing */}
      <div>
        <p className="text-sm font-medium text-muted-foreground mb-1">Project Name</p>
        {isEditingName ? (
          <div>
            <Input
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={() => {
                if (nameValue !== initialNameValue && nameValue.trim()) {
                  updateProjectInline.mutate({
                    projectId: project.id,
                    data: { name: nameValue.trim() }
                  });
                }
                queueMicrotask(() => {
                  setIsEditingName(false);
                });
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.currentTarget.blur();
                } else if (e.key === 'Escape') {
                  setNameValue(initialNameValue);
                  setIsEditingName(false);
                }
              }}
              disabled={updateProjectInline.isPending}
              autoFocus
              className="text-2xl font-bold max-w-2xl"
              data-testid="name-input"
            />
            <InlineEditActions
              onCancel={() => {
                setNameValue(initialNameValue);
                setIsEditingName(false);
              }}
            />
          </div>
        ) : (
          <div className="relative inline-block group">
            <h2
              className="text-2xl font-bold cursor-pointer hover:ring-1 hover:ring-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 rounded px-2 py-1 pr-10 transition-all duration-200 inline-block"
              role="button"
              tabIndex={0}
              onClick={() => {
                setNameValue(project.name);
                setInitialNameValue(project.name);
                setIsEditingName(true);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setNameValue(project.name);
                  setInitialNameValue(project.name);
                  setIsEditingName(true);
                }
              }}
              title="Click to edit project name"
              data-testid="name-display"
            >
              {project.name}
            </h2>
            <Pencil
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 right-2 h-5 w-5 text-gray-400 group-hover:text-gray-600 transition-colors"
            />
              </div>
            )}
              </div>

      {/* Phase 7 Batch 6 Task 2: Project Overview Card - Two-column layout */}
      {/* 9.5c: Wrapped in CollapsibleSection */}
      <CollapsibleSection
        title={project.is_template ? 'Template Overview' : isProposalStage(project.workflow_stage) ? 'Proposal Overview' : 'Project Overview'}
        icon={<FileText className="w-5 h-5 text-sky-600" />}
        summary={getOverviewSummary()}
        defaultOpen={true}
        testId="project-overview"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column: Project details fields */}
            <div className="space-y-4">
              {/* Project Number - hide for proposals (they don't have one yet), inline editable (Phase 9.6b) */}
              {project.project_number && (
                <div className="grid gap-2">
                  <Label className="text-sm font-medium text-gray-500">Project Number</Label>
                  {isEditingProjectNumber ? (
                    <Input
                      type="text"
                      value={projectNumberValue}
                      onChange={(e) => setProjectNumberValue(e.target.value)}
                      onBlur={() => {
                        if (projectNumberValue !== initialProjectNumberValue && projectNumberValue.trim()) {
                          updateProjectInline.mutate({
                            projectId: project.id,
                            data: { project_number: projectNumberValue.trim() }
                          });
                        }
                        setIsEditingProjectNumber(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        } else if (e.key === 'Escape') {
                          setProjectNumberValue(initialProjectNumberValue);
                          setIsEditingProjectNumber(false);
                        }
                      }}
                      autoFocus
                      className="text-lg font-semibold h-auto py-1 px-2 w-32"
                    />
                  ) : (
                    <div
                      className="text-lg font-semibold hover:ring-1 hover:ring-gray-400 rounded px-1 py-1 cursor-pointer inline-block"
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setProjectNumberValue(project.project_number || '');
                        setInitialProjectNumberValue(project.project_number || '');
                        setIsEditingProjectNumber(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setProjectNumberValue(project.project_number || '');
                          setInitialProjectNumberValue(project.project_number || '');
                          setIsEditingProjectNumber(true);
                        }
                      }}
                    >
                      #{project.project_number}
                    </div>
                  )}
                </div>
              )}

              {/* Category - inline editable (Phase 7.5) */}
              <div className="grid gap-2">
                <Label className="text-sm font-medium text-gray-500">Category</Label>
                {isEditingCategory ? (
                  <Select
                    value={project.category || '__none__'}
                    onValueChange={(newCategory) => {
                      updateProjectInline.mutate({
                        projectId: project.id,
                        data: { category: newCategory === '__none__' ? null : newCategory }
                      });
                      setIsEditingCategory(false);
                    }}
                    onOpenChange={(open) => !open && setIsEditingCategory(false)}
                  >
                    <SelectTrigger className="w-[180px]" data-testid="category-select-overview">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">No category</SelectItem>
                      {(projectCategoryOptions ?? []).map((category) => (
                        <SelectItem key={category.id} value={category.value}>
                          {category.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <div
                    className="inline-flex items-center gap-2 hover:ring-1 hover:ring-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 rounded transition-all duration-200 px-1 py-1 cursor-pointer group"
                    role="button"
                    tabIndex={0}
                    onClick={() => setIsEditingCategory(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsEditingCategory(true);
                      }
                    }}
                    title="Click to edit category"
                    data-testid="category-display-overview"
                  >
                    {project.category ? (
                      <Badge variant="outline" className="cursor-pointer">
                        {project.category}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground italic cursor-pointer">No category</span>
                    )}
                    <Pencil
                      aria-hidden
                      className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    />
                  </div>
                )}
              </div>

              {/* 9.7a: Client section removed - client is now shown as first scope in the Scopes list */}

              {/* Workflow Stage - inline editable from Batch 3, but replaced with "Convert to Project" button for proposals (Phase 7.3.4 Improvement #4) */}
              <div className="grid gap-2">
                <Label className="text-sm font-medium text-gray-500">Workflow Stage</Label>
                {project.is_template ? (
                  // Templates: Show static "Template" badge (no workflow stages)
                  <Badge variant="secondary" className="w-fit">Template</Badge>
                ) : project.workflow_stage && (
                  isProposalStage(project.workflow_stage) ? (
                    // Phase 7.3.4 Improvement #4: Show "Convert to Project" button instead of dropdown for proposals
                    // Prevents confusing error when trying to change workflow_stage directly (backend blocks this)
                    <div className="flex items-center gap-2">
                      <ProjectStageBadge
                        workflowStage={project.workflow_stage as WorkflowStage}
                        updatedAt={project.updated_at}
                      />
                      <Button
                        onClick={() => setShowConvertModal(true)}
                        variant="outline"
                        size="sm"
                        data-testid="convert-proposal-button-inline"
                      >
                        Convert to Project
                      </Button>
                    </div>
                  ) : (
                    // Normal workflow stage editing for non-proposals
                    isEditingWorkflowStage ? (
                      <WorkflowStageSelect
                        value={project.workflow_stage as WorkflowStage}
                        onChange={(newStage) => {
                          updateProjectInline.mutate({
                            projectId: project.id,
                            data: { workflow_stage: newStage }
                          });
                          setIsEditingWorkflowStage(false);
                        }}
                        onCancel={() => setIsEditingWorkflowStage(false)}
                        disabled={updateProjectInline.isPending}
                        className="w-[220px]"
                      />
                    ) : (
                      <div
                        onClick={() => setIsEditingWorkflowStage(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setIsEditingWorkflowStage(true);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        className="relative cursor-pointer inline-flex items-center gap-2 hover:ring-1 hover:ring-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 rounded transition-all duration-200 px-1 py-1 group"
                        title="Click to edit workflow stage"
                        data-testid="workflow-stage-badge-overview"
                      >
                        <ProjectStageBadge
                          workflowStage={project.workflow_stage as WorkflowStage}
                          updatedAt={project.updated_at}
                        />
                        <Pencil
                          aria-hidden
                          className="h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                        />
                      </div>
                    )
                  )
                )}
              </div>

              {/* Phase 7.5.10: Proposal Status/History - show for proposals and converted projects */}
              {!project.is_template && (isProposalStage(project.workflow_stage) || project.proposal_accepted_at) && (
                <div className="grid gap-2" data-testid="proposal-status-section">
                  <Label className="text-sm font-medium text-gray-500">
                    {isProposalStage(project.workflow_stage) ? 'Proposal Status' : 'Proposal History'}
                  </Label>
                  <div className="space-y-2 text-sm">
                    {/* Sent date */}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-20">Sent:</span>
                      {project.proposal_sent_at ? (
                        <span>
                          {new Date(project.proposal_sent_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      ) : isProposalStage(project.workflow_stage) ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">Not yet sent</span>
                          <Button
                            onClick={() => sendProposal.mutate(project.id)}
                            variant="outline"
                            size="sm"
                            disabled={sendProposal.isPending}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Mark as Sent
                          </Button>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                    {/* Accepted date */}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-20">Accepted:</span>
                      {project.proposal_accepted_at ? (
                        <span>
                          {new Date(project.proposal_accepted_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                    {/* Phase 9.6: Declined/Lost date */}
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500 w-20">Lost:</span>
                      {project.proposal_declined_at ? (
                        <span>
                          {new Date(project.proposal_declined_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </span>
                      ) : isProposalStage(project.workflow_stage) && project.workflow_stage !== 'proposal_declined' ? (
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">—</span>
                          <Button
                            onClick={() => declineProposal.mutate(project.id)}
                            variant="outline"
                            size="sm"
                            disabled={declineProposal.isPending}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Mark as Lost
                          </Button>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                    {/* Email Send History - inline indicator (replaces Send Proposal button) */}
                    {isProposalStage(project.workflow_stage) && (
                      <div className="flex items-center gap-2 pt-2">
                        <span className="text-gray-500 w-20">Email:</span>
                        <EmailSendIndicator
                          entityType="proposal"
                          entityId={project.id}
                          onViewHistory={() => {
                            setSendProposalDialogInitialTab('history');
                            setSendProposalDialogOpen(true);
                          }}
                        />
                      </div>
                    )}
                    {/* Phase 10c.9: Per-Project Cover Image Override */}
                    {isProposalStage(project.workflow_stage) && (
                      <div className="pt-2">
                        <ProjectCoverImageUpload projectId={project.id} />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Phase 9.5f: Status field removed - workflow_stage is now the primary state indicator */}

              {/* Site Address - moved from Project Details card */}
              {/* v0.12.27 Item 6: relabel "Address" -> "Site Address" so it's distinct from billing_address */}
              <div className="grid gap-2">
                <Label className="text-sm font-medium text-gray-500">Site Address</Label>
                {isEditingAddress ? (
                  <div>
                    <Input
                      value={addressValue}
                      onChange={(e) => setAddressValue(e.target.value)}
                      onBlur={() => {
                        saveSiteAddress();
                        queueMicrotask(() => {
                          setIsEditingAddress(false);
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.currentTarget.blur();
                        } else if (e.key === 'Escape') {
                          siteSaveStarted.current = true;
                          setAddressValue(initialAddressValue);
                          setIsEditingAddress(false);
                        }
                      }}
                      disabled={updateProjectInline.isPending}
                      autoFocus
                      className="mt-1"
                      data-testid="address-input"
                    />
                    <InlineEditActions
                      onCancel={() => {
                        setAddressValue(initialAddressValue);
                        setIsEditingAddress(false);
                        siteSaveStarted.current = false;
                      }}
                      showSaveButton={true}
                      onSave={() => {
                        saveSiteAddress();
                        setIsEditingAddress(false);
                      }}
                    />
                  </div>
                ) : (
                  <div className="relative group">
                    <p
                      className={`${project.address ? '' : 'text-muted-foreground italic'} cursor-pointer hover:ring-1 hover:ring-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 rounded px-2 py-1 pr-8 transition-all duration-200`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        const currentAddress = project.address || '';
                        siteSaveStarted.current = false;
                        setAddressValue(currentAddress);
                        setInitialAddressValue(currentAddress);
                        setIsEditingAddress(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          const currentAddress = project.address || '';
                          siteSaveStarted.current = false;
                          setAddressValue(currentAddress);
                          setInitialAddressValue(currentAddress);
                          setIsEditingAddress(true);
                        }
                      }}
                      title="Click to edit address"
                      data-testid="address-display"
                    >
                      {project.address || 'No address'}
                    </p>
                    <Pencil
                      aria-hidden
                      className="absolute top-1/2 -translate-y-1/2 right-2 h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    />
                  </div>
                )}
                {/* v0.12.27 Item 6: clarify this is the physical site, not the billing address */}
                <p className="text-xs text-muted-foreground">
                  The project's physical location. Different from the client's billing address.
                </p>
              </div>

              {/* Notes - moved from Project Details card */}
              <div className="grid gap-2">
                <Label className="text-sm font-medium text-gray-500">Notes</Label>
                {isEditingNotes ? (
                  <div>
                    <Textarea
                      value={notesValue}
                      onChange={(e) => setNotesValue(e.target.value)}
                      onBlur={() => {
                        if (notesValue !== initialNotesValue) {
                          updateProjectInline.mutate({
                            projectId: project.id,
                            data: { notes: notesValue || null }
                          });
                        }
                        queueMicrotask(() => {
                          setIsEditingNotes(false);
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setNotesValue(initialNotesValue);
                          setIsEditingNotes(false);
                        }
                      }}
                      disabled={updateProjectInline.isPending}
                      autoFocus
                      className="mt-1 min-h-[100px]"
                      placeholder="Add project notes..."
                      data-testid="notes-input"
                    />
                    <InlineEditActions
                      onCancel={() => {
                        setNotesValue(initialNotesValue);
                        setIsEditingNotes(false);
                      }}
                      showEnterHint={false}
                    />
                  </div>
                ) : (
                  <div className="relative group">
                    <p
                      className={`${project.notes ? 'whitespace-pre-wrap' : 'text-muted-foreground italic'} cursor-pointer hover:ring-1 hover:ring-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400 rounded px-2 py-1 pr-8 transition-all duration-200`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        const currentNotes = project.notes || '';
                        setNotesValue(currentNotes);
                        setInitialNotesValue(currentNotes);
                        setIsEditingNotes(true);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          const currentNotes = project.notes || '';
                          setNotesValue(currentNotes);
                          setInitialNotesValue(currentNotes);
                          setIsEditingNotes(true);
                        }
                      }}
                      title="Click to edit notes"
                      data-testid="notes-display"
                    >
                      {project.notes || 'No notes'}
                    </p>
                    <Pencil
                      aria-hidden
                      className="absolute top-2 right-2 h-4 w-4 text-gray-400 group-hover:text-gray-600 transition-colors"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Action Summary + Financial Summary (nested cards) */}
            <div className="space-y-6">
              {/* Templates and Proposals: Hide action summary (not relevant for early-stage proposals) */}
              {!project.is_template && !isProposalStage(project.workflow_stage) && (
                <ActionSummaryWidget
                  waitingOnUsCount={actionSummary.waitingOnUsCount}
                  waitingOnThemCount={actionSummary.waitingOnThemCount}
                  scopesWithActionNeeded={actionSummary.scopesWithActionNeeded}
                  billableItems={billableItems}
                  permits={permitsData}
                  overdueAcceptanceItems={
                    // R2: Filter deliverable items when the deliverables add-in is disabled.
                    // Widget remains hardcoded to 4 sections; filtering happens at the call site.
                    visibleSections.includes('deliverables')
                      ? overdueAcceptanceItems
                      : overdueAcceptanceItems.filter((item) => item.itemType !== 'deliverable')
                  }
                  // MED-04: Click handlers for navigating to specific sections
                  onScopeClick={(scopeId) => {
                    setExpandedScopeId(scopeId);
                    // Scroll to the scopes section after state update
                    setTimeout(() => {
                      document.getElementById('scopes-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                  }}
                  onBillableItemClick={(itemId) => {
                    // Scroll to billable items section
                    document.getElementById('billable-items-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  onPermitClick={(permitId) => {
                    // Scroll to permits section
                    document.getElementById('permits-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  // F6: clicking the Waiting on Us / Them counts navigates to
                  // the relevant section. Prefers scopes when scope-level items
                  // dominate, otherwise falls back to client actions (the primary
                  // driver of these counts on portal-active projects).
                  onWaitingOnUsClick={() => {
                    const target = waitingOnUsScopes.length > 0
                      ? document.getElementById('scopes-section')
                      : document.querySelector('[data-testid="section-client-actions"]');
                    (target as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  onWaitingOnThemClick={() => {
                    const target = waitingOnThemScopes.length > 0
                      ? document.getElementById('scopes-section')
                      : document.querySelector('[data-testid="section-client-actions"]');
                    (target as HTMLElement | null)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                />
              )}
              {/* Phase 7.5.6: Show proposed totals for templates/proposals, confirmed for active projects */}
              <FinancialSummaryTable
                confirmedTotal={project.confirmed_total || 0}
                invoicedTotal={project.is_template ? undefined : (project.invoiced_total || 0)}
                unbilledTotal={project.is_template ? undefined : (project.unbilled_total || 0)}
                paidTotal={project.is_template ? undefined : (project.paid_fee || 0)}
                unpaidTotal={project.is_template ? undefined : (project.unpaid_fee || 0)}
                proposedTotal={project.proposed_total || 0}
                proposedRequiredTotal={project.proposed_required_total || 0}
                isTemplate={project.is_template}
                isProposal={isProposalStage(project.workflow_stage)}
              />

              {/* Phase 8.3.7: Project Understanding - Inline editable */}
              {/* 9.9d CONTRACT-01: Collapsible, collapsed by default */}
              {/* Post-proposal: wrap in muted disclosure; during proposal: show standalone */}
              {!isProposalStage(project.workflow_stage) && !project.is_template && (
                <details
                  className="group bg-slate-50/40 rounded-lg border border-slate-100 cursor-pointer"
                  data-testid="section-proposal-details"
                >
                  <summary className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors select-none p-4">
                    <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-0 -rotate-90" aria-hidden />
                    {project.proposal_accepted_at ? (
                      <>
                        <span>Proposal accepted {new Date(project.proposal_accepted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        <span className="text-xs text-slate-500 font-normal">
                          — {feeAssumptionsData?.total ?? feeAssumptionsData?.fee_assumptions?.length ?? 0} item{(feeAssumptionsData?.total ?? feeAssumptionsData?.fee_assumptions?.length ?? 0) !== 1 ? 's' : ''}, {formatCurrency(project.confirmed_total)}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>Proposal Details</span>
                        <span className="text-xs text-slate-500 font-normal">Project Understanding · Fee Assumptions</span>
                      </>
                    )}
                  </summary>

                  <div className="space-y-4 px-4 pb-4">
                    <CollapsibleSection
                      title="Project Understanding"
                      icon={<FileText className="w-4 h-4 text-gray-500" />}
                      defaultOpen={false}
                      summary={project.project_understanding ? "Click to view" : "Not set"}
                      testId="section-proposal-understanding"
                      className="border-gray-100 bg-white"
                    >
                      <ProjectUnderstandingEdit
                        projectId={project.id}
                        projectUnderstanding={project.project_understanding}
                        onSave={handleProjectUnderstandingSave}
                      />
                    </CollapsibleSection>

                    {/* Fee Assumptions - Phase 7.3.3, 9.9d CONTRACT-03 */}
                    <CollapsibleSection
                      title="Fee Assumptions"
                      icon={<DollarSign className="w-4 h-4 text-gray-500" />}
                      defaultOpen={false}
                      summary="Click to view/edit"
                      testId="section-proposal-assumptions"
                      className="border-gray-100 bg-white"
                    >
                      <FeeAssumptionsList projectId={project.id} />
                    </CollapsibleSection>
                  </div>
                </details>
              )}

              {/* During proposal stage: keep original standalone behavior */}
              {isProposalStage(project.workflow_stage) && !project.is_template && (
                <>
                  <CollapsibleSection
                    title="Project Understanding"
                    icon={<FileText className="w-4 h-4 text-gray-500" />}
                    defaultOpen={false}
                    summary={project.project_understanding ? "Click to view" : "Not set"}
                    testId="section-project-understanding"
                    className="border-gray-100"
                  >
                    <ProjectUnderstandingEdit
                      projectId={project.id}
                      projectUnderstanding={project.project_understanding}
                      onSave={handleProjectUnderstandingSave}
                    />
                  </CollapsibleSection>

                  {/* Fee Assumptions - Phase 7.3.3, 9.9d CONTRACT-03 */}
                  {/* 9.9d CONTRACT-01: Collapsible, collapsed by default */}
                  {/* 9.9d CONTRACT-03: Individual list items with drag-drop reordering */}
                  <CollapsibleSection
                    title="Fee Assumptions"
                    icon={<DollarSign className="w-4 h-4 text-gray-500" />}
                    defaultOpen={false}
                    summary="Click to view/edit"
                    testId="section-fee-assumptions"
                    className="border-gray-100"
                  >
                    <FeeAssumptionsList projectId={project.id} />
                  </CollapsibleSection>

                  {/* V120: CA Scope - Construction Administration scope definition */}
                  <CollapsibleSection
                    title="Construction Administration Scope"
                    icon={<FileCheck className="w-4 h-4 text-gray-500" />}
                    defaultOpen={false}
                    summary={project.ca_scope ? "Configured" : "Not set"}
                    testId="section-ca-scope"
                    className="border-gray-100"
                  >
                    <CAScopeSection
                      projectId={project.id}
                      caScope={(project.ca_scope ?? null) as CAScopeConfig | null}
                      onUpdate={(scope) => {
                        updateProjectInline.mutate({
                          projectId: project.id,
                          data: { ca_scope: scope as Record<string, unknown> }
                        });
                      }}
                      isSaving={updateProjectInline.isPending}
                    />
                  </CollapsibleSection>
                </>
              )}

              {/* Template view: no proposal details disclosure */}
              {project.is_template && (
                <>
                  <CollapsibleSection
                    title="Project Understanding"
                    icon={<FileText className="w-4 h-4 text-gray-500" />}
                    defaultOpen={false}
                    summary={project.project_understanding ? "Click to view" : "Not set"}
                    testId="section-project-understanding"
                    className="border-gray-100"
                  >
                    <ProjectUnderstandingEdit
                      projectId={project.id}
                      projectUnderstanding={project.project_understanding}
                      onSave={handleProjectUnderstandingSave}
                    />
                  </CollapsibleSection>

                  {/* Fee Assumptions - Phase 7.3.3, 9.9d CONTRACT-03 */}
                  {/* 9.9d CONTRACT-01: Collapsible, collapsed by default */}
                  {/* 9.9d CONTRACT-03: Individual list items with drag-drop reordering */}
                  <CollapsibleSection
                    title="Fee Assumptions"
                    icon={<DollarSign className="w-4 h-4 text-gray-500" />}
                    defaultOpen={false}
                    summary="Click to view/edit"
                    testId="section-fee-assumptions"
                    className="border-gray-100"
                  >
                    <FeeAssumptionsList projectId={project.id} />
                  </CollapsibleSection>
                </>
              )}
            </div>
          </div>
      </CollapsibleSection>

      {/* v0.15: Hidden-section deep-link banner — when ?section= targets an add-in
          that resolved hidden, explain why and offer "Show section" (unless hard-gated). */}
      {hiddenDeepLinkSection && (
        <HiddenSectionBanner
          key={hiddenDeepLinkSection.key}
          label={hiddenDeepLinkSection.label}
          hardGated={hiddenDeepLinkSection.hardGated}
          onShow={() => showSection(hiddenDeepLinkSection.key)}
        />
      )}

      {/* Issue #7: Sections grouped into collapsible group headers */}
      {/* Template view: flat rendering without group headers */}
      {project.is_template ? (
        <div className="space-y-6" data-testid="scopes-items-row">
          {/* R5 (v0.14): Billing tracks panel — self-hides for 1-scope projects */}
          <BillingTracksPanel projectId={project.id} />
          {/* Phase 5 Batch 3: Project Scopes Section */}
          {/* 9.7a: Client scope now included in list (sorted first) */}
          {/* MED-04: ID for scroll-to navigation from Action Summary */}
          {/* 9.5c: Wrapped in CollapsibleSection */}
          {/* 9.15d: Controlled mode for context-aware opening */}
          <CollapsibleSection
            title={`Scopes (${scopes.length})`}
            icon={<Layers className="w-5 h-5 text-slate-600" />}
            summary={getScopesSummary()}
            open={openSections.scopes}
            onOpenChange={handleSectionChange('scopes')}
            testId="section-scopes"
          >
            <div id="scopes-section">
              <ProjectScopesList
                scopes={sortedScopes}
                onAddScope={() => setShowAddScopeModal(true)}
                onDeleteScope={handleDeleteScope}
                onUpdateStatus={handleUpdateScopeStatus}
                // Issue #13: Full edit mode props
                onUpdate={handleUpdateScope}
                organizations={organizations}
                scopeTypes={scopeTypes}
                contacts={contacts}
                onOrganizationChange={handleOrganizationChange}
                // Phase 6 Batch 2: Accordion state
                expandedScopeId={expandedScopeId}
                onToggleScope={(scopeId) => {
                  const newExpandedId = expandedScopeId === scopeId ? null : scopeId;
                  setExpandedScopeId(newExpandedId);
                  // 9.7bc Polish: Auto-load contacts when scope is expanded
                  if (newExpandedId) {
                    const scope = scopes.find(s => s.id === newExpandedId);
                    if (scope?.organization_id) {
                      setSelectedOrgIdForContacts(scope.organization_id);
                    }
                  }
                }}
                // Phase 7.3.3 Phase 2.5: Template support
                isTemplate={project.is_template}
                // 9.5c: Hide header since CollapsibleSection provides it
                hideHeader={true}
                // Phase 9.7bc: Set primary client scope
                onSetPrimary={handleSetPrimary}
                // Phase 9.7bc: Multi-contact management
                onAddContact={handleAddContact}
                onRemoveContact={handleRemoveContact}
                onSetPrimaryContact={handleSetPrimaryContact}
                onSetInvoiceContact={handleSetInvoiceContact}
                projectId={project.id}
                // Phase 10d.3: Portal access badges on scope contacts
                portalMemberEmails={hasPortalManagement ? portalMemberEmails : undefined}
              />
            </div>
          </CollapsibleSection>

          {/* Billable Items (template view) */}
          {/* MED-04: ID for scroll-to navigation from Action Summary */}
          {/* 9.5c: Wrapped in CollapsibleSection */}
          {/* 9.15d: Controlled mode for context-aware opening */}
          <CollapsibleSection
            title={project.is_template ? 'Template Items' : isProposalStage(project.workflow_stage) ? 'Proposed Items' : 'Billable Items'}
            icon={<DollarSign className="w-5 h-5 text-emerald-600" />}
            summary={getBillableItemsSummary()}
            open={openSections['billable-items']}
            onOpenChange={handleSectionChange('billable-items')}
            testId="section-billable-items"
          >
            <div id="billable-items-section">
              {/* Header with description and actions */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {project.is_template
                      ? 'Example items that will be copied to new projects'
                      : isProposalStage(project.workflow_stage)
                        ? 'Items proposed for this project'
                        : 'Confirmed work for this project'}
                  </p>
                  {/* Phase 7.5.6: Show proposed totals for templates/proposals */}
                  {(project.is_template || isProposalStage(project.workflow_stage)) && (
                    <div className="mt-2 text-sm font-medium" data-testid="billable-items-proposed-totals">
                      <span className="text-emerald-600">
                        Proposed: ${((project.proposed_total || 0) as number).toLocaleString()}
                      </span>
                      <span className="text-muted-foreground ml-2">
                        (Required: ${((project.proposed_required_total || 0) as number).toLocaleString()})
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  {/* Phase 8.6: Quick-add from templates */}
                  <BillableItemTemplateQuickAdd
                    projectId={project?.id || ''}
                    projectNumber={project?.project_number}
                    project={project}
                    disabled={isCreatingNewItem}
                  />
                  <Button
                    onClick={() => setIsCreatingNewItem(true)}
                    disabled={isCreatingNewItem}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Item
                  </Button>
                </div>
              </div>
              {/* Table content */}
              {itemsLoading ? (
                <div className="space-y-3">
                  {[...Array(3)].map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : itemsError ? (
                <div className="text-center py-12 text-red-600 bg-red-50 rounded-lg">
                  <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                  <p className="font-medium">Error loading billable items</p>
                  <p className="text-sm mt-1 text-red-500">
                    {(itemsError as Error)?.message || 'Unknown error'}
                  </p>
                  <p className="text-xs mt-2 text-gray-500">
                    Project ID: {project?.id || 'N/A'}
                  </p>
                </div>
              ) : billableItems.length === 0 && !isCreatingNewItem ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No billable items yet</p>
                  <p className="text-sm mt-1">Add items to track confirmed work</p>
                </div>
              ) : (
                <BillableItemsTable
                  items={billableItems}
                  projectId={project?.id || ''}
                  projectNumber={project?.project_number ?? null}
                  workflowStage={project?.workflow_stage}
                  onUpdate={(item) => {
                    // Optimistically update handled by mutation in BillableItemInlineForm
                  }}
                  onDelete={(itemId) => {
                    const item = billableItems.find(i => i.id === itemId);
                    if (item) {
                      setItemToDelete(item);
                    }
                  }}
                  isCreatingNew={isCreatingNewItem}
                  onCancelCreate={() => setIsCreatingNewItem(false)}
                  onCreateSuccess={() => setIsCreatingNewItem(false)}
                />
              )}
            </div>
          </CollapsibleSection>
        </div>
      ) : (
        /* Non-template view: sections organized into collapsible groups */
        <div className="space-y-0" data-testid="scopes-items-row">

          {/* ── Group: Project Management ─────────────────────────────────── */}
          <div className={showGrouped ? "border-t border-border/50 pt-4 mt-4" : ""}>
            {showGrouped && (
              <SectionGroupHeader
                label="Project Management"
                accentColor="bg-rose-400/70"
                isExpanded={isGroupOpen('group-project-management')}
                onToggle={() => toggleGroup('group-project-management')}
              />
            )}
            {(!showGrouped || isGroupOpen('group-project-management')) && (
              <div className="space-y-4 mt-2">
                {/* R5 (v0.14): Billing tracks panel — self-hides for 1-scope projects */}
                <BillingTracksPanel projectId={project.id} />
                {/* Phase 5 Batch 3: Project Scopes Section */}
                {/* 9.7a: Client scope now included in list (sorted first) */}
                {/* MED-04: ID for scroll-to navigation from Action Summary */}
                {/* 9.5c: Wrapped in CollapsibleSection */}
                {/* 9.15d: Controlled mode for context-aware opening */}
                <CollapsibleSection
                  title={`Scopes (${scopes.length})`}
                  icon={<Layers className="w-5 h-5 text-slate-600" />}
                  summary={getScopesSummary()}
                  open={openSections.scopes}
                  onOpenChange={handleSectionChange('scopes')}
                  testId="section-scopes"
                >
                  <div id="scopes-section">
                    <ProjectScopesList
                      scopes={sortedScopes}
                      onAddScope={() => setShowAddScopeModal(true)}
                      onDeleteScope={handleDeleteScope}
                      onUpdateStatus={handleUpdateScopeStatus}
                      // Issue #13: Full edit mode props
                      onUpdate={handleUpdateScope}
                      organizations={organizations}
                      scopeTypes={scopeTypes}
                      contacts={contacts}
                      onOrganizationChange={handleOrganizationChange}
                      // Phase 6 Batch 2: Accordion state
                      expandedScopeId={expandedScopeId}
                      onToggleScope={(scopeId) => {
                        const newExpandedId = expandedScopeId === scopeId ? null : scopeId;
                        setExpandedScopeId(newExpandedId);
                        // 9.7bc Polish: Auto-load contacts when scope is expanded
                        if (newExpandedId) {
                          const scope = scopes.find(s => s.id === newExpandedId);
                          if (scope?.organization_id) {
                            setSelectedOrgIdForContacts(scope.organization_id);
                          }
                        }
                      }}
                      // Phase 7.3.3 Phase 2.5: Template support
                      isTemplate={project.is_template}
                      // 9.5c: Hide header since CollapsibleSection provides it
                      hideHeader={true}
                      // Phase 9.7bc: Set primary client scope
                      onSetPrimary={handleSetPrimary}
                      // Phase 9.7bc: Multi-contact management
                      onAddContact={handleAddContact}
                      onRemoveContact={handleRemoveContact}
                      onSetPrimaryContact={handleSetPrimaryContact}
                      onSetInvoiceContact={handleSetInvoiceContact}
                      projectId={project.id}
                      // Phase 10d.3: Portal access badges on scope contacts
                      portalMemberEmails={hasPortalManagement ? portalMemberEmails : undefined}
                    />
                  </div>
                </CollapsibleSection>

                {/* Phase 10b.7: Deliverables Section — R2: visibility gated by useVisibleSections */}
                {visibleSections.includes('deliverables') && (
                  <CollapsibleSection
                    title={deliverablesCount > 0 ? `Deliverables (${deliverablesCount})` : 'Deliverables'}
                    icon={<Layers className="w-5 h-5 text-violet-600" />}
                    open={openSections.deliverables}
                    onOpenChange={handleSectionChange('deliverables')}
                    testId="section-deliverables"
                    headerActions={makeSectionKebab('deliverables')}
                  >
                    <DeliverablesSection projectId={project?.id || ''} />
                  </CollapsibleSection>
                )}

                {/* Phase 10c.10: Team Members Section — R2: visibility gated by useVisibleSections */}
                {visibleSections.includes('team') && (
                  <CollapsibleSection
                    title={projectMembersCount > 0 ? `Team (${projectMembersCount})` : 'Team'}
                    icon={<Users className="w-5 h-5 text-blue-600" />}
                    open={openSections.team}
                    onOpenChange={handleSectionChange('team')}
                    testId="section-team"
                    headerActions={makeSectionKebab('team')}
                  >
                    <ProjectMembersSection projectId={project?.id || ''} />
                  </CollapsibleSection>
                )}

                {/* Permits & Submissions — hide for proposals */}
                {!isProposalStage(project.workflow_stage) && (
                  <CollapsibleSection
                    title={`Permits & Submissions (${permitsData.length})`}
                    icon={<FileCheck className="w-5 h-5 text-teal-600" />}
                    summary={getPermitsSummary()}
                    open={openSections.permits}
                    onOpenChange={handleSectionChange('permits')}
                    testId="section-permits"
                  >
                    <div id="permits-section">
                      <PermitsList projectId={project?.id || ''} hideHeader={true} />
                    </div>
                  </CollapsibleSection>
                )}

              </div>
            )}
          </div>

          {/* Invoices — core section, always rendered outside PM group gate */}
          <CollapsibleSection
            title={`Invoices (${invoices.length})`}
            icon={<Receipt className="w-5 h-5 text-indigo-600" />}
            summary={getInvoicesSummary()}
            open={openSections.invoices}
            onOpenChange={handleSectionChange('invoices')}
            testId="section-invoices"
          >
            <ProjectInvoicesList projectId={project?.id || ''} hideHeader={true} />
          </CollapsibleSection>

          {/* ── Group: Financials ─────────────────────────────────────────── */}
          <div className={showGrouped ? "border-t border-border/50 pt-4 mt-4" : ""}>
            {showGrouped && (
              <SectionGroupHeader
                label="Financials"
                accentColor="bg-emerald-400/70"
                isExpanded={isGroupOpen('group-financials')}
                onToggle={() => toggleGroup('group-financials')}
              />
            )}
            {(!showGrouped || isGroupOpen('group-financials')) && (
              <div className="space-y-4 mt-2">
                {/* Billable Items */}
                {/* MED-04: ID for scroll-to navigation from Action Summary */}
                {/* 9.5c: Wrapped in CollapsibleSection */}
                {/* 9.15d: Controlled mode for context-aware opening */}
                <CollapsibleSection
                  title={isProposalStage(project.workflow_stage) ? 'Proposed Items' : 'Billable Items'}
                  icon={<DollarSign className="w-5 h-5 text-emerald-600" />}
                  summary={getBillableItemsSummary()}
                  open={openSections['billable-items']}
                  onOpenChange={handleSectionChange('billable-items')}
                  testId="section-billable-items"
                >
                  <div id="billable-items-section">
                    {/* Header with description and actions */}
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {isProposalStage(project.workflow_stage)
                            ? 'Items proposed for this project'
                            : 'Confirmed work for this project'}
                        </p>
                        {/* Phase 7.5.6: Show proposed totals for proposals */}
                        {isProposalStage(project.workflow_stage) && (
                          <div className="mt-2 text-sm font-medium" data-testid="billable-items-proposed-totals">
                            <span className="text-emerald-600">
                              Proposed: ${((project.proposed_total || 0) as number).toLocaleString()}
                            </span>
                            <span className="text-muted-foreground ml-2">
                              (Required: ${((project.proposed_required_total || 0) as number).toLocaleString()})
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {/* Phase 8.6: Quick-add from templates */}
                        <BillableItemTemplateQuickAdd
                          projectId={project?.id || ''}
                          projectNumber={project?.project_number}
                          project={project}
                          disabled={isCreatingNewItem}
                        />
                        <Button
                          onClick={() => setIsCreatingNewItem(true)}
                          disabled={isCreatingNewItem}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Item
                        </Button>
                      </div>
                    </div>
                    {/* Table content */}
                    {itemsLoading ? (
                      <div className="space-y-3">
                        {[...Array(3)].map((_, i) => (
                          <Skeleton key={i} className="h-16 w-full" />
                        ))}
                      </div>
                    ) : itemsError ? (
                      <div className="text-center py-12 text-red-600 bg-red-50 rounded-lg">
                        <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
                        <p className="font-medium">Error loading billable items</p>
                        <p className="text-sm mt-1 text-red-500">
                          {(itemsError as Error)?.message || 'Unknown error'}
                        </p>
                        <p className="text-xs mt-2 text-gray-500">
                          Project ID: {project?.id || 'N/A'}
                        </p>
                      </div>
                    ) : billableItems.length === 0 && !isCreatingNewItem ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <p>No billable items yet</p>
                        <p className="text-sm mt-1">Add items to track confirmed work</p>
                      </div>
                    ) : (
                      <BillableItemsTable
                        items={billableItems}
                        projectId={project?.id || ''}
                        projectNumber={project?.project_number ?? null}
                        workflowStage={project?.workflow_stage}
                        onUpdate={(item) => {
                          // Optimistically update handled by mutation in BillableItemInlineForm
                        }}
                        onDelete={(itemId) => {
                          const item = billableItems.find(i => i.id === itemId);
                          if (item) {
                            setItemToDelete(item);
                          }
                        }}
                        isCreatingNew={isCreatingNewItem}
                        onCancelCreate={() => setIsCreatingNewItem(false)}
                        onCreateSuccess={() => setIsCreatingNewItem(false)}
                      />
                    )}
                  </div>
                </CollapsibleSection>

                {/* Phase 11a: Schedule of Values (SOV) — R2: visibility gated by useVisibleSections */}
                {visibleSections.includes('sov') && (
                  <CollapsibleSection
                    title="Schedule of Values (SOV)"
                    icon={<DollarSign className="w-5 h-5 text-emerald-600" />}
                    open={openSections.sov}
                    onOpenChange={handleSectionChange('sov')}
                    testId="section-sov"
                    headerActions={makeSectionKebab('sov')}
                  >
                    <SOVTab projectId={project?.id || ''} />
                  </CollapsibleSection>
                )}
              </div>
            )}
          </div>

          {/* ── Group: Construction Admin ─────────────────────────────────── */}
          <div className={showGrouped ? "border-t border-border/50 pt-4 mt-4" : ""}>
            {showGrouped && (
              <SectionGroupHeader
                label="Construction Admin"
                accentColor="bg-sky-400/70"
                isExpanded={isGroupOpen('group-construction-admin')}
                onToggle={() => toggleGroup('group-construction-admin')}
              />
            )}
            {(!showGrouped || isGroupOpen('group-construction-admin')) && (
              <div className="space-y-4 mt-2">
                {/* Site Visits Section - Phase 10d.1 — R2: visibility gated by useVisibleSections */}
                {visibleSections.includes('site-visits') && (
                  <CollapsibleSection
                    title={siteVisitsCount > 0 ? `Site Visits (${siteVisitsCount})` : 'Site Visits'}
                    icon={<Camera className="w-5 h-5 text-green-600" />}
                    open={openSections.siteVisits}
                    onOpenChange={handleSectionChange('siteVisits')}
                    testId="section-site-visits"
                    headerActions={makeSectionKebab('site-visits')}
                  >
                    <SiteVisitsSection projectId={project?.id || ''} />
                  </CollapsibleSection>
                )}

                {/* Submittals Section — R2: visibility gated by useVisibleSections */}
                {visibleSections.includes('submittals') && (
                  <CollapsibleSection
                    title={submittalsCount > 0 ? `Submittals (${submittalsCount})` : 'Submittals'}
                    icon={<FileStack className="w-5 h-5 text-amber-600" />}
                    open={openSections.submittals}
                    onOpenChange={handleSectionChange('submittals')}
                    testId="section-submittals"
                    headerActions={makeSectionKebab('submittals')}
                  >
                    <SubmittalsSection projectId={project?.id || ''} />
                  </CollapsibleSection>
                )}

                {/* RFIs Section - Phase 10d.2 — R2: visibility gated by useVisibleSections */}
                {visibleSections.includes('rfis') && (
                  <CollapsibleSection
                    title={rfisCount > 0 ? `RFIs (${rfisCount})` : 'RFIs'}
                    icon={<MessageSquare className="w-5 h-5 text-indigo-600" />}
                    open={openSections.rfis}
                    onOpenChange={handleSectionChange('rfis')}
                    testId="section-rfis"
                    headerActions={makeSectionKebab('rfis')}
                  >
                    <RFIsList projectId={project?.id || ''} hideHeader />
                  </CollapsibleSection>
                )}

                {/* Supplemental Instructions — R2: visibility gated by useVisibleSections */}
                {visibleSections.includes('sis') && (
                  <CollapsibleSection
                    title={sisCount > 0 ? `Supplemental Instructions (${sisCount})` : 'Supplemental Instructions'}
                    icon={<FileText className="w-5 h-5 text-teal-600" />}
                    open={openSections.sis}
                    onOpenChange={handleSectionChange('sis')}
                    testId="section-sis"
                    headerActions={makeSectionKebab('sis')}
                  >
                    <SIsList projectId={project?.id || ''} hideHeader />
                  </CollapsibleSection>
                )}

                {/* planTEK: Action Items (formerly "Client Actions") — PM checklist for portal clients (portal_management gate) */}
                {/* R2: Label renamed to "Action Items". Code key stays 'client-actions' (no DB change). */}
                {hasPortalManagement && (
                  <CollapsibleSection
                    title={clientActionsCount > 0 ? `Action Items (${clientActionsCount})` : 'Action Items'}
                    icon={<ClipboardList className="w-5 h-5 text-violet-600" />}
                    open={openSections.clientActions}
                    onOpenChange={handleSectionChange('clientActions')}
                    testId="section-client-actions"
                  >
                    <ClientActionsSection projectId={project?.id || ''} />
                  </CollapsibleSection>
                )}

                {/* R1: Documents Tab — full document library grouped by folder */}
                <CollapsibleSection
                  title="Documents"
                  icon={<FolderOpen className="w-5 h-5 text-orange-500" />}
                  open={openSections.documents}
                  onOpenChange={handleSectionChange('documents')}
                  testId="section-documents"
                >
                  <DocumentsSection projectId={projectId} />
                </CollapsibleSection>
              </div>
            )}
          </div>

          {/* ── Ungrouped: Client Portal + Messages ─────────────────────── */}
          {/* Portal improvements: Client Portal inline management (ungrouped per spec) */}
          {/* R2: portal hard-gated by portal_management feature flag AND useVisibleSections. */}
          {/* External-view code path does NOT inherit section_config filtering (Phase 10d follow-up). */}
          {hasPortalManagement && visibleSections.includes('portal') && (
            <CollapsibleSection
              title="Client Portal"
              icon={<Globe className="w-5 h-5 text-sky-600" />}
              open={openSections.portal}
              onOpenChange={handleSectionChange('portal')}
              testId="section-portal"
              className="mt-4"
              headerActions={makeSectionKebab('portal')}
            >
              <ProjectPortalSection
                projectId={project?.id || ''}
                projectName={project?.name || ''}
              />
            </CollapsibleSection>
          )}

          {/* planTEK: Messages Section — R2: visibility gated by useVisibleSections */}
          {visibleSections.includes('messages') && (
            <CollapsibleSection
              title={messagesCount > 0 ? `Messages (${messagesCount})` : 'Messages'}
              icon={<MessageCircle className="w-5 h-5 text-violet-600" />}
              open={openSections.messages}
              onOpenChange={handleSectionChange('messages')}
              testId="section-messages"
              className="mt-4"
              headerActions={makeSectionKebab('messages')}
            >
              <ProjectMessagesPanel projectId={project?.id || ''} />
            </CollapsibleSection>
          )}

          {/* R3: "+ Add module" affordance — visible when there are hidden add-ins */}
          {canEdit && (
            <AddModuleAffordance
              project={project}
              hiddenAddIns={pickableHiddenAddIns}
              defaultStackVisibility={defaultStackVisibility}
            />
          )}
        </div>
      )}

      {/* Phase 5 Batch 3: Add Scope Modal */}
      {/* Batch 7.3.4: Pass isTemplate to handle template-specific UI */}
      <AddScopeModal
        open={showAddScopeModal}
        onOpenChange={(open) => {
          setShowAddScopeModal(open);
          if (!open) setAddScopeDefaultType(null);
        }}
        projectId={project?.id || ''}
        isTemplate={project?.is_template}
        defaultScopeType={addScopeDefaultType}
        onScopeAdded={() => setShowAddScopeModal(false)}
      />

      {/* R3: Remove section confirmation dialog — shown only when section has data */}
      <AlertDialog
        open={!!confirmRemoveSection}
        onOpenChange={(open) => { if (!open) setConfirmRemoveSection(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hide {confirmRemoveSection?.label}?</AlertDialogTitle>
            <AlertDialogDescription>
              This section has existing data. It will be hidden from the project view but the data is preserved.
              You can re-add it at any time using the "+ Add section" link.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmRemoveSection) {
                  executeRemoveSection(confirmRemoveSection.key);
                  setConfirmRemoveSection(null);
                }
              }}
              data-testid="confirm-remove-section"
            >
              Hide
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Archive Project/Proposal/Template Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5 text-muted-foreground" />
              Archive {project.is_template ? 'Template' : isProposalStage(project.workflow_stage) ? 'Proposal' : 'Project'}
            </DialogTitle>
            <DialogDescription className="space-y-2">
              <p>Are you sure you want to archive "{project.name}"?</p>
              <p className="text-sm text-muted-foreground">
                The {project.is_template ? 'template' : isProposalStage(project.workflow_stage) ? 'proposal' : 'project'} and all its data will be preserved but hidden from active views. You can restore it at any time from the Archived tab.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteProject.isPending}
            >
              {deleteProject.isPending ? 'Archiving...' : `Archive ${project.is_template ? 'Template' : isProposalStage(project.workflow_stage) ? 'Proposal' : 'Project'}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Billable Item Confirmation Dialog (Bug #12 fix) */}
      <Dialog open={!!itemToDelete} onOpenChange={(open) => !open && setItemToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Billable Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this billable item? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setItemToDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteItem}
              disabled={deleteBillableItem.isPending}
            >
              {deleteBillableItem.isPending ? 'Deleting...' : 'Delete Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 7 Batch 7.3: Convert Proposal Modal */}
      <ConvertProposalModal
        open={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        projectId={project.id}
        projectName={project.name}
        billableItems={billableItems}
        initialCategory={project.category}
      />

      {/* Phase 7.3.3 Phase 2.2: Convert to Template Modal */}
      <AlertDialog open={showConvertToTemplateModal} onOpenChange={setShowConvertToTemplateModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save &quot;{project.name}&quot; as Template?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new template and navigate to the template page where you can edit it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            <div className="space-y-1 text-sm">
              <p className="font-medium">What will happen:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Create a NEW template with &quot;{project.name} (Template)&quot;</li>
                <li>Copy billable items and scopes to the new template</li>
                <li>Clear all scope organizations on the template (filled in when using template)</li>
                <li>Navigate to the template page where you can update the name, notes, and other details</li>
                <li>Your original project will remain unchanged</li>
              </ul>
            </div>

            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
              <p className="text-sm text-blue-900">
                ✓ Your original project remains intact - this creates a copy as a template
              </p>
            </div>

            <p className="text-xs text-muted-foreground">
              Note: This is a &quot;Save As&quot; operation - your original project will remain unchanged.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConvertToTemplate}>
              Save as Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Phase 7.3.3 Phase 2.6: Copy Template Modal */}
      <AlertDialog open={showCopyTemplateModal} onOpenChange={setShowCopyTemplateModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create a copy of &quot;{project.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new template based on this one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              The copy will include:
            </p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>All scopes (with NULL organizations)</li>
              <li>All billable items</li>
              <li>Fee assumptions</li>
            </ul>
            <p className="text-muted-foreground">
              You can edit the copy after it&apos;s created.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCopyTemplate}>
              Copy Template
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {reviewIntent && <InvoiceChangeReview intent={reviewIntent}
        focusedProjectIds={project?.id ? [project.id] : []}
        onClose={() => { setReviewIntent(null); siteSaveStarted.current = false; }}
        onApplied={() => { setReviewIntent(null); siteSaveStarted.current = false; }} />}
      </div>
    </DashboardLayout>
  );
}
